import { Module } from '../../types';

export const dspyModule2: Module = {
  id: 'dspy-module2',
  title: 'DSPy Programming Fundamentals',
  description:
    'Master signatures, explore all built-in modules, learn composition patterns, and build sophisticated multi-stage programs.',
  duration: '4-5 hours',
  content: `# Module 2: DSPy Programming Fundamentals

Welcome to the heart of DSPy programming! If Module 1 was your introduction to the philosophy and basic concepts, Module 2 is where you become a true DSPy architect. This is where the magic happens - where simple concepts transform into powerful, intelligent systems.

Think of this module as your journey from apprentice to craftsperson. You'll learn not just what each tool does, but when to use it, how to combine tools effectively, and how to build systems that are both sophisticated and maintainable. By the end, you'll think like a DSPy expert, seeing problems not as monolithic challenges but as opportunities to orchestrate multiple forms of intelligence.

## 🎯 Learning Objectives: Your Journey to Mastery

By the end of this module, you will have transformed from someone who knows DSPy concepts to someone who can architect DSPy solutions. Specifically, you will:

- **Master the Art of Signatures**: Move beyond simple input-output specifications to create sophisticated, type-safe interfaces that guide AI behavior with precision and clarity
- **Wield Every DSPy Module**: Understand not just what each built-in module does, but develop the intuition for when and how to use each one effectively
- **Compose Like a Pro**: Create custom modules that combine simpler components into sophisticated systems, following proven patterns and best practices
- **Build Intelligent Pipelines**: Design multi-stage systems with branching logic, error handling, and adaptive behavior that can tackle complex, real-world problems
- **Integrate the Real World**: Connect your AI systems to external tools, databases, and APIs, creating agents that can both think and act

## 📚 Signatures in Depth

### Understanding Signatures: The DNA of DSPy Programs

Think of signatures as the DNA of your DSPy programs. Just as DNA contains the instructions for building complex organisms, signatures contain the instructions for building intelligent language model interactions. They define not just what inputs and outputs your program expects, but how the language model should think about and approach the task.

Signatures are the foundation of DSPy - they define the contract between your code and the language model. But they're much more than simple function signatures. They're intelligent specifications that guide the language model's behavior, provide context for complex tasks, and enable DSPy's powerful optimization capabilities.

### The Evolution from Prompts to Signatures

Imagine you're a project manager trying to explain a complex task to a new team member. With traditional prompting, it's like giving them a single, long email with everything jumbled together. With DSPy signatures, it's like providing them with a clear job description, expected deliverables, and quality standards - all structured and easy to understand.

\`\`\`python
# Traditional prompting - like a confusing email
prompt = "Given this product review, tell me if it's positive or negative, predict the star rating, and extract key themes. Here's the review: {review_text}"

# DSPy signature - like a clear job description
class ReviewAnalysis(dspy.Signature):
    """Analyze product reviews comprehensively."""
    
    review_text: str = dspy.InputField(desc="The customer review text")
    product_category: str = dspy.InputField(desc="Product category context")
    
    sentiment: Literal['positive', 'negative', 'neutral'] = dspy.OutputField()
    predicted_rating: int = dspy.OutputField(desc="Predicted star rating 1-5")
    key_themes: list[str] = dspy.OutputField(desc="Main themes mentioned")
    summary: str = dspy.OutputField(desc="Brief review summary")
\`\`\`

The signature version is not only clearer but also enables DSPy to automatically generate better prompts, validate outputs, and optimize performance.

#### The Signature Spectrum: Choosing Your Level of Precision

Signatures in DSPy exist on a spectrum from simple to sophisticated. Think of it like giving directions to someone: you could say "go downtown" (simple), or provide turn-by-turn GPS instructions with landmarks and alternatives (sophisticated). Both work, but the detailed version gets better, more consistent results.

**Level 1: Simple Inline Signatures - The Quick Start**

When you're prototyping or dealing with straightforward tasks, simple inline signatures are your best friend. They're like writing a quick note to yourself - fast, clear, and perfect for getting started. Use these when you want to test an idea quickly or when the task is genuinely simple.

**Level 2: Typed Inline Signatures - Adding Structure**

As your tasks become more complex, you'll want to add type information. This is like moving from "pick up some groceries" to "pick up 2 apples, 1 gallon of milk, and fresh bread." The additional structure helps both you and the language model understand exactly what to expect.

**Level 3: Class-Based Signatures - The Professional Standard**

For production systems and complex tasks, class-based signatures are essential. They're like detailed project specifications that leave nothing to chance. This is where DSPy really shines - you can provide comprehensive context, constraints, and validation that ensure consistent, high-quality results.

**Real-World Example: Building a Customer Support System**

Let's say you're building a customer support email analyzer. Here's how you might progress through the signature spectrum:

\`\`\`python
# Level 1: Quick prototype to test the concept
simple = dspy.Predict("email -> category, sentiment")

# Level 2: More specific about what we want
typed = dspy.Predict("email: str -> category: str, sentiment: str, urgency: int")

# Level 3: Production-ready with full context and validation
class AnalyzeCustomerEmail(dspy.Signature):
    """Extract key information from customer emails to route and prioritize support requests.
    
    This signature is designed for customer service automation. It analyzes incoming emails
    to determine the type of issue, emotional tone, and urgency level to ensure proper
    routing and response timing.
    """
    
    email_text: str = dspy.InputField(
        desc="The complete email content including any quoted text or signatures"
    )
    subject: str = dspy.InputField(
        desc="Email subject line, often contains important context about the issue type"
    )
    customer_tier: str = dspy.InputField(
        desc="Customer tier (premium, standard, basic) for prioritization context", 
        default="standard"
    )
    
    sentiment: Literal['positive', 'negative', 'neutral', 'frustrated'] = dspy.OutputField(
        desc="Overall emotional tone - frustrated indicates high urgency situations"
    )
    urgency: int = dspy.OutputField(
        desc="Urgency level from 1-5 where 5 requires immediate response within 1 hour"
    )
    category: str = dspy.OutputField(
        desc="Primary issue category: technical, billing, feature-request, complaint, or praise"
    )
    summary: str = dspy.OutputField(
        desc="2-3 sentence summary capturing the main issue and any specific details mentioned"
    )
    requires_human: bool = dspy.OutputField(
        desc="True if this issue requires human intervention rather than automated response"
    )
\`\`\`

Notice how the class-based version provides context not just about what to extract, but why it matters and how it will be used. This additional context dramatically improves the quality and consistency of results.

### Advanced Type Annotations: Precision in Communication

As your DSPy systems become more sophisticated, you'll find yourself working with complex data structures. Maybe you're analyzing financial data with nested structures, processing user profiles with optional fields, or handling multi-modal inputs that could be text, images, or structured data. This is where advanced type annotations become crucial.

Think of type annotations as a shared language between you, your code, and the language model. When you specify that a field should be a \`List[ProductInfo]\` rather than just \`list\`, you're providing three layers of value:

1. **Code Safety**: Your IDE and type checkers can catch errors before runtime
2. **Documentation**: Future developers (including yourself) understand the expected structure
3. **Model Guidance**: The language model understands exactly what format to produce

**The Power of Pydantic Integration**

DSPy's integration with Pydantic models is particularly powerful for business applications. Pydantic doesn't just provide type hints - it provides validation, serialization, and clear error messages. When you define a \`ProductInfo\` model with price constraints, you're ensuring that your AI system produces structurally valid, business-ready data.

**When to Use Complex Types**

Use complex type annotations when:
- Your data has meaningful structure (e.g., addresses, products, financial records)
- You need validation (e.g., email formats, price ranges, dates)
- You're integrating with existing systems that expect specific data formats
- You want clear error messages when something goes wrong

**Real-World Scenario: E-commerce Inventory Analysis**

Imagine you're building an AI system for e-commerce inventory management. You need to analyze product data, sales trends, and make restocking recommendations. Here's how advanced types make this robust and maintainable:

\`\`\`python
from typing import List, Dict, Optional, Literal, Union
from pydantic import BaseModel, Field

# Using Pydantic models for complex types
class ProductInfo(BaseModel):
    name: str
    price: float = Field(gt=0, description="Price in USD")
    category: str
    in_stock: bool

class InventoryAnalysis(dspy.Signature):
    """Analyze product inventory and make recommendations."""
    
    products: List[ProductInfo] = dspy.InputField()
    sales_data: Dict[str, int] = dspy.InputField(desc="Product ID to units sold")
    
    restock_needed: List[str] = dspy.OutputField(desc="Product IDs needing restock")
    trending_categories: List[str] = dspy.OutputField()
    recommendations: str = dspy.OutputField()

# Using Union types for flexibility
class FlexibleQuery(dspy.Signature):
    """Handle multiple input formats."""
    
    query: Union[str, List[str]] = dspy.InputField()
    mode: Literal['single', 'batch'] = dspy.InputField()
    
    results: Union[str, List[str]] = dspy.OutputField()
\`\`\`

### Multi-Modal Signatures

\`\`\`python
class ImageAnalysis(dspy.Signature):
    """Analyze images with optional context."""
    
    image: dspy.Image = dspy.InputField(desc="Image to analyze")
    context: Optional[str] = dspy.InputField(default=None)
    
    description: str = dspy.OutputField()
    objects_detected: List[str] = dspy.OutputField()
    scene_type: str = dspy.OutputField()

# Usage with images
analyzer = dspy.Predict(ImageAnalysis)
result = analyzer(
    image=dspy.Image(path="product_photo.jpg"),
    context="This is from our electronics catalog"
)
\`\`\`

### Dynamic Signatures

\`\`\`python
def create_extraction_signature(fields: List[str]):
    """Dynamically create signatures based on required fields."""
    
    class DynamicExtraction(dspy.Signature):
        text: str = dspy.InputField()
    
    # Add fields dynamically
    for field in fields:
        setattr(DynamicExtraction, field, dspy.OutputField(str))
    
    DynamicExtraction.__doc__ = f"Extract {', '.join(fields)} from text."
    return DynamicExtraction

# Create specialized extractors
invoice_extractor = create_extraction_signature(['invoice_number', 'total', 'date'])
contact_extractor = create_extraction_signature(['name', 'email', 'phone'])
\`\`\`

### Signature Inheritance and Composition

\`\`\`python
# Base signature for common fields
class BaseAnalysis(dspy.Signature):
    """Base signature for all analysis tasks."""
    text: str = dspy.InputField()
    confidence: float = dspy.OutputField(desc="Confidence score 0-1")

# Inherit and extend
class SentimentAnalysis(BaseAnalysis):
    """Analyze sentiment with confidence."""
    sentiment: Literal['positive', 'negative', 'neutral'] = dspy.OutputField()
    key_phrases: List[str] = dspy.OutputField()

class TopicAnalysis(BaseAnalysis):
    """Extract topics with confidence."""
    main_topics: List[str] = dspy.OutputField()
    category: str = dspy.OutputField()
\`\`\`

### Best Practices for Signatures: The Art of Clear Communication

Writing good signatures is like being a great communicator - it's not just about what you say, but how you say it. A well-crafted signature serves as both a contract and a guide, telling the language model not just what to do, but how to think about the problem.

Think of signatures as the difference between giving someone a vague task ("make this better") versus a clear, actionable request ("improve the readability of this paragraph by shortening sentences and using more concrete examples"). The latter gives much better results because it provides context, constraints, and clear success criteria.

\`\`\`python
# DO: Be specific with descriptions
class GoodSignature(dspy.Signature):
    """Extract pricing information from product descriptions."""
    
    description: str = dspy.InputField(
        desc="Product description containing price information"
    )
    
    base_price: float = dspy.OutputField(
        desc="Base price before any discounts"
    )
    discount_percentage: Optional[float] = dspy.OutputField(
        desc="Discount percentage if mentioned, None otherwise"
    )
    final_price: float = dspy.OutputField(
        desc="Final price after discounts"
    )

# DON'T: Vague or overly complex signatures
class BadSignature(dspy.Signature):
    """Do stuff with data."""  # Too vague
    
    data: Any = dspy.InputField()  # Too generic
    
    # Too many unrelated outputs
    result1: str = dspy.OutputField()
    result2: List[Dict[str, Any]] = dspy.OutputField()
    result3: float = dspy.OutputField()
    # ... 10 more fields
\`\`\`

## 🔧 Core DSPy Modules: Your AI Programming Toolkit

Imagine you're building a house. You wouldn't use the same tool for every job - you need a hammer for nails, a saw for cutting, and a drill for holes. DSPy modules work the same way. Each module is a specialized tool designed for specific types of thinking and problem-solving.

The beauty of DSPy modules lies in their composability. Just as a skilled carpenter combines different tools to build something magnificent, you can combine DSPy modules to create sophisticated AI systems that can reason, act, and solve complex problems.

### The DSPy Module Family Tree

Think of this as your AI toolkit. Each module has its own personality and strengths:

\`\`\`
dspy.Module (base)
├── dspy.Predict          - The straightforward worker: "Just give me an answer"
├── dspy.ChainOfThought   - The careful thinker: "Let me think through this step by step"
├── dspy.ProgramOfThought - The programmer: "I'll write code to solve this"
├── dspy.ReAct           - The versatile agent: "I'll think, act, and adapt as needed"
├── dspy.MultiChainComparison - The wise council: "Let's consider multiple perspectives"
├── dspy.Retry           - The persistent one: "If at first you don't succeed..."
└── Custom Modules       - Your domain experts: "I specialize in exactly what you need"
\`\`\`

### Understanding Module Personalities

Each DSPy module has a distinct "personality" that makes it suitable for different types of tasks:

**dspy.Predict** is like that efficient colleague who gets straight to the point. Give it a task, and it delivers results without overthinking. Perfect for classification, simple Q&A, or any task where you need quick, direct answers.

**dspy.ChainOfThought** is the methodical problem-solver who shows their work. It's like having a smart friend who not only gives you the answer but walks you through their reasoning. This transparency makes it invaluable for complex reasoning tasks where you need to understand the "why" behind the answer.

**dspy.ReAct** is the versatile agent - part detective, part scientist. It can reason about problems, take actions to gather more information, and adapt its approach based on what it discovers. Think of it as your AI research assistant that can actually do things, not just think about them.

**dspy.MultiChainComparison** is like having a panel of experts debate a topic. It generates multiple independent reasoning paths and helps you see different perspectives on the same problem. Perfect for complex decisions where you want to consider multiple angles.

### Deep Dive: Predict Module - The Reliable Workhorse

The \`dspy.Predict\` module is like the Swiss Army knife of DSPy - simple, reliable, and surprisingly versatile. While it might seem basic compared to modules like \`ChainOfThought\` or \`ReAct\`, it's often exactly what you need for many real-world tasks.

**When Predict Shines**

Think of \`Predict\` as your go-to module for:
- **Classification tasks**: "Is this email spam or not?"
- **Simple transformations**: "Convert this formal text to casual tone"
- **Quick factual queries**: "What's the capital of this country?"
- **Structured extraction**: "Pull the key details from this document"

The beauty of \`Predict\` lies in its efficiency and directness. It doesn't add reasoning overhead when you don't need it, making it faster and more cost-effective for straightforward tasks.

**Understanding Predict's Configuration Options**

Every parameter in \`Predict\` serves a purpose in real-world applications:

- **max_len**: Controls output length, crucial for cost management and ensuring concise responses
- **temperature**: Balances creativity vs consistency - lower for factual tasks, higher for creative ones
- **n**: Generates multiple outputs for comparison or fallback options

**Real-World Example: Content Moderation Pipeline**

Imagine you're building a content moderation system for a social media platform. You need to quickly classify millions of posts for potential policy violations. Here's where \`Predict\` excels:

\`\`\`python
# Basic usage
predict = dspy.Predict("question -> answer")

# Advanced configuration
predict_advanced = dspy.Predict(
    signature="question -> answer",
    max_len=500,      # Maximum output length
    temperature=0.7,  # Creativity level
    n=3              # Generate multiple outputs
)

# Using with typed signature
class FactCheck(dspy.Signature):
    claim: str = dspy.InputField()
    evidence: List[str] = dspy.InputField()
    
    verdict: bool = dspy.OutputField()
    explanation: str = dspy.OutputField()

fact_checker = dspy.Predict(FactCheck)
result = fact_checker(
    claim="The Earth is flat",
    evidence=["NASA photos show Earth is round", "Ships disappear over horizon"]
)
\`\`\`

### Deep Dive: ChainOfThought - The Transparent Thinker

If \`Predict\` is the efficient worker who just gets things done, \`ChainOfThought\` is the thoughtful colleague who shows their work. This isn't just about getting better answers (though it often does) - it's about building trust, enabling debugging, and creating AI systems that can explain their reasoning to humans.

**Why Reasoning Matters in Production Systems**

In many real-world applications, the "how" is as important as the "what." Consider these scenarios:

- **Medical diagnosis**: Doctors need to understand the reasoning behind AI recommendations
- **Financial decisions**: Regulators require explainable AI for loan approvals
- **Legal analysis**: Lawyers need to trace the logic behind document analysis
- **Educational systems**: Students learn better when they can follow the reasoning process

\`ChainOfThought\` makes AI reasoning visible and auditable, which is crucial for building trust and meeting compliance requirements.

**The Psychology of Step-by-Step Reasoning**

Humans naturally think better when they break complex problems into steps. The same is true for language models. By encouraging step-by-step reasoning, \`ChainOfThought\` often produces more accurate results, especially for:

- **Multi-step calculations**: Math problems, financial analyses, statistical reasoning
- **Complex logical reasoning**: Legal arguments, scientific analysis, strategic planning
- **Creative problem-solving**: Brainstorming, design thinking, innovative solutions

**When to Choose ChainOfThought Over Predict**

Use \`ChainOfThought\` when:
- The problem requires multiple logical steps
- You need to explain the reasoning to users
- Accuracy is more important than speed
- The domain is complex or specialized
- You're debugging why an AI system makes certain decisions

**Real-World Success Story: Legal Document Analysis**

A law firm needed to analyze contracts for compliance issues. Using \`Predict\` alone, they got good results but couldn't explain the reasoning to clients. By switching to \`ChainOfThought\`, they could show exactly which contract clauses triggered which compliance concerns, building client trust and enabling lawyers to verify the AI's work.

\`\`\`python
# ChainOfThought adds reasoning automatically
cot = dspy.ChainOfThought("question -> answer")

# Custom reasoning format
class DetailedReasoning(dspy.Signature):
    """Solve problems with detailed step-by-step reasoning."""
    
    problem: str = dspy.InputField()
    constraints: List[str] = dspy.InputField(desc="Any constraints to consider")
    
    reasoning_steps: List[str] = dspy.OutputField(desc="Step by step reasoning")
    final_answer: str = dspy.OutputField()
    confidence: float = dspy.OutputField()

detailed_solver = dspy.ChainOfThought(DetailedReasoning)

result = detailed_solver(
    problem="Plan a route from NYC to LA with stops",
    constraints=["Must visit Chicago", "Maximum 3 days", "Budget $500"]
)

print("Steps:")
for i, step in enumerate(result.reasoning_steps, 1):
    print(f"{i}. {step}")
print(f"\\nAnswer: {result.final_answer}")
print(f"Confidence: {result.confidence}")
\`\`\`

### Deep Dive: ProgramOfThought - The Computational Mathematician

If \`ChainOfThought\` is the transparent thinker, then \`ProgramOfThought\` is the computational mathematician - a module that doesn't just reason about problems, but actually writes and executes code to solve them. This is where DSPy transcends traditional language model limitations and becomes a true computational partner.

**The Revolutionary Concept: From Words to Working Code**

Traditional language models can tell you how to solve a math problem, but they can't actually compute the answer reliably. They might explain calculus beautifully but struggle with precision when calculating integrals. \`ProgramOfThought\` bridges this gap by generating executable Python code that performs the actual computation.

Think of it as having a mathematician who not only understands the theory but can also implement the solution in code and run it to get exact results. This combination of conceptual understanding and computational execution is incredibly powerful.

**When ProgramOfThought Shines**

\`ProgramOfThought\` excels in scenarios where precision and computation are critical:

- **Mathematical Problem Solving**: Calculus, statistics, complex equations where exact answers matter
- **Data Analysis Tasks**: Statistical calculations, data transformations, numerical analysis
- **Scientific Computations**: Physics simulations, engineering calculations, research problems
- **Financial Modeling**: Risk calculations, portfolio optimization, actuarial computations
- **Algorithmic Challenges**: Problems requiring iterative solutions or complex logic

**The Trust Factor: Verifiable Results**

One of the biggest advantages of \`ProgramOfThought\` is trustworthiness. When a language model tells you "the answer is 42," you have to trust it. When \`ProgramOfThought\` shows you the code that calculated 42, you can verify, modify, and understand exactly how that answer was derived.

**Real-World Success Story: Financial Risk Assessment**

A financial services company needed to calculate Value at Risk (VaR) for complex portfolios. Traditional approaches required expensive specialized software and expert analysts. Using \`ProgramOfThought\`, they could:

1. Describe the risk calculation requirements in natural language
2. Generate Python code implementing the mathematical models
3. Execute the code with real portfolio data
4. Get precise, auditable results with full transparency

The system not only produced accurate calculations but also generated documentation explaining the methodology, making it valuable for regulatory compliance.

**Understanding the Three-Part Output**

\`ProgramOfThought\` typically produces three crucial components:

1. **The Code**: Executable Python that solves the problem
2. **The Result**: The actual computed answer
3. **The Explanation**: Human-readable description of the approach and solution

This triple output makes it perfect for educational contexts, regulatory environments, and collaborative work where transparency matters.

**Best Practices for ProgramOfThought**

- **Be Specific with Problem Descriptions**: Clear problem statements lead to better code generation
- **Consider Code Safety**: In production, implement proper code sandboxing and validation
- **Validate Results**: While the code is usually correct, always verify critical calculations
- **Leverage the Explanation**: Use the natural language explanation to understand and communicate the approach

\`\`\`python
# Generate and execute code
class MathProblem(dspy.Signature):
    """Solve complex math problems by writing Python code."""
    
    problem: str = dspy.InputField()
    
    code: str = dspy.OutputField(desc="Python code to solve the problem")
    result: float = dspy.OutputField(desc="Numerical result")
    explanation: str = dspy.OutputField(desc="Explanation of the solution")

math_solver = dspy.ProgramOfThought(MathProblem)

result = math_solver(
    problem="Find the area under the curve y=x^2 from x=0 to x=5"
)

print(f"Generated code:\\n{result.code}")
print(f"Result: {result.result}")
print(f"Explanation: {result.explanation}")
\`\`\`

### Deep Dive: ReAct (Reasoning + Acting) - The Intelligent Agent

\`ReAct\` represents the evolution of AI from passive responder to active agent. If other DSPy modules are like consultants who provide advice, \`ReAct\` is like a capable assistant who can both think through problems and take concrete actions to solve them. This is where DSPy becomes truly agentic - capable of autonomous problem-solving in dynamic environments.

**The Power of the Think-Act Loop**

Traditional AI systems follow a simple pattern: receive input, process, provide output. \`ReAct\` introduces a revolutionary approach: Think → Act → Observe → Think → Act. This iterative cycle mirrors how humans solve complex problems in the real world.

Consider how you might research a complex question:
1. **Think**: "I need information about X"
2. **Act**: Search for relevant sources
3. **Observe**: Review the search results
4. **Think**: "I need more specific information about Y"
5. **Act**: Search with refined terms
6. **Observe**: Find the exact information needed
7. **Think**: "Now I can formulate a comprehensive answer"

\`ReAct\` brings this natural problem-solving approach to AI systems.

**When ReAct Becomes Essential**

\`ReAct\` shines in scenarios where static knowledge isn't enough:

- **Dynamic Information Gathering**: When you need current data, real-time information, or context-specific details
- **Multi-Step Problem Solving**: Complex tasks that require breaking down into subtasks and taking intermediate actions
- **Interactive Environments**: Situations where the AI needs to respond to changing conditions
- **Tool-Intensive Workflows**: Tasks requiring coordination of multiple external tools and services
- **Exploratory Analysis**: When the path to the solution isn't predetermined and requires investigation

**The Intelligence of Iterative Refinement**

What makes \`ReAct\` particularly powerful is its ability to learn and adapt within a single interaction. Unlike traditional systems that commit to an approach upfront, \`ReAct\` can:

- **Course-correct** when initial actions don't yield expected results
- **Refine strategies** based on intermediate observations
- **Combine insights** from multiple tool interactions
- **Adapt to unexpected situations** that arise during execution

**Real-World Transformation: Customer Service Revolution**

A major telecommunications company replaced their rigid customer service system with a \`ReAct\`-based agent. The transformation was remarkable:

**Before (Traditional System):**
- Customer: "My internet isn't working"
- System: Provides generic troubleshooting steps
- Result: 60% of issues required human escalation

**After (ReAct Agent):**
1. **Think**: Customer reports connectivity issue - need to diagnose
2. **Act**: Check customer's service status in real-time
3. **Observe**: Service shows active, but with intermittent drops
4. **Think**: Intermittent drops suggest local network issue
5. **Act**: Run diagnostic on customer's specific connection
6. **Observe**: High packet loss detected on specific network segment
7. **Think**: Network segment issue identified - can provide targeted solution
8. **Act**: Schedule specific technician dispatch and provide interim workaround

**Result**: 85% issue resolution without human escalation, dramatically improved customer satisfaction.

**The Art of Tool Orchestration**

\`ReAct\` doesn't just use tools - it orchestrates them intelligently. It understands:

- **Tool Selection**: Which tool is best for each specific subtask
- **Sequence Planning**: The optimal order of tool usage
- **Error Handling**: What to do when tools fail or return unexpected results
- **Context Preservation**: How to maintain state across multiple tool interactions

**Designing Effective ReAct Systems**

**Tool Selection Strategy:**
- Choose tools that provide clear, structured outputs
- Ensure tools have good error handling and feedback
- Design tools to be composable and work well together

**Iteration Management:**
- Set appropriate \`max_iters\` to balance thoroughness with efficiency
- Design signatures that encourage productive reasoning chains
- Include mechanisms for the agent to recognize when it has sufficient information

**Safety Considerations:**
- Implement proper access controls for sensitive tools
- Add validation for tool outputs before using them in decisions
- Design fallback strategies for when tools are unavailable

\`\`\`python
# Define tools for the agent
def search_database(query: str) -> str:
    """Search internal database for information."""
    # Simulate database search
    return f"Found 3 records matching '{query}'"

def calculate(expression: str) -> float:
    """Evaluate mathematical expressions."""
    return eval(expression)  # In production, use safe evaluation

def send_email(to: str, subject: str, body: str) -> str:
    """Send an email (simulated)."""
    return f"Email sent to {to}"

# Create ReAct agent with tools
agent = dspy.ReAct(
    signature="task -> result",
    tools=[search_database, calculate, send_email],
    max_iters=5  # Maximum reasoning/action cycles
)

# Complex task requiring multiple tools
result = agent(
    task="Find all customers who ordered in the last month, calculate their total spending, and email those who spent over $1000"
)

# ReAct with custom signature
class CustomerOutreach(dspy.Signature):
    """Plan and execute customer outreach tasks."""
    
    task_description: str = dspy.InputField()
    customer_segment: str = dspy.InputField()
    
    actions_taken: List[str] = dspy.OutputField()
    results_summary: str = dspy.OutputField()
    next_steps: List[str] = dspy.OutputField()

outreach_agent = dspy.ReAct(
    CustomerOutreach,
    tools=[search_database, send_email],
    max_iters=10
)
\`\`\`

### Deep Dive: MultiChainComparison - The Wise Council

\`MultiChainComparison\` embodies the wisdom of seeking multiple perspectives before making important decisions. If \`ChainOfThought\` is like consulting one expert, \`MultiChainComparison\` is like convening a panel of experts, each bringing their unique viewpoint to bear on the same problem. This is where DSPy harnesses the power of diverse reasoning to produce more robust, well-considered outcomes.

**The Philosophy of Cognitive Diversity**

In the real world, the best decisions often emerge from considering multiple perspectives. A business decision might look different to a financial analyst, a risk manager, and a market researcher. Each brings valid insights, and the synthesis of their viewpoints typically produces better outcomes than any single perspective alone.

\`MultiChainComparison\` replicates this cognitive diversity within a single AI system. By generating multiple independent reasoning chains, it captures the benefits of diverse thinking while maintaining the efficiency of automated processing.

**The Science Behind Multiple Perspectives**

Research in cognitive science shows that diverse thinking approaches lead to:
- **Reduced Bias**: Different reasoning paths can counteract systematic errors
- **Enhanced Creativity**: Multiple approaches often uncover novel solutions
- **Improved Robustness**: Solutions that satisfy multiple perspectives tend to be more resilient
- **Better Error Detection**: Inconsistencies between reasoning chains highlight potential issues

\`MultiChainComparison\` leverages these principles algorithmically, creating a form of "artificial cognitive diversity."

**When MultiChainComparison Excels**

This module shines in scenarios where single-perspective analysis might miss crucial considerations:

- **Strategic Business Decisions**: Market entry, product launches, investment choices
- **Risk Assessment**: Evaluating threats from multiple angles (financial, operational, regulatory)
- **Creative Problem Solving**: Brainstorming sessions where diverse ideas are valuable
- **Quality Assurance**: Cross-checking critical analyses with multiple approaches
- **Complex Diagnostics**: Medical, technical, or investigative scenarios with multiple possible explanations

**The Power of Algorithmic Debate**

Think of \`MultiChainComparison\` as orchestrating an internal debate between different reasoning approaches. Each "participant" in this debate:

1. **Approaches the problem independently**: No chain is influenced by others during generation
2. **Brings unique perspectives**: Different temperature settings and random seeds ensure diversity
3. **Produces complete reasoning**: Each chain provides a full analysis, not just partial views
4. **Contributes to synthesis**: The system intelligently combines insights from all chains

**Real-World Impact: Investment Decision Making**

A venture capital firm implemented \`MultiChainComparison\` for startup evaluation. Their traditional process involved sequential expert reviews, which often suffered from anchoring bias - later reviewers were influenced by earlier opinions.

**The MultiChain Approach:**
- **Chain 1**: Financial perspective - revenue models, market size, scalability
- **Chain 2**: Technical perspective - innovation, competitive advantages, execution risk
- **Chain 3**: Market perspective - timing, competition, customer adoption patterns

**Results:**
- **30% improvement** in investment performance over 2 years
- **Reduced groupthink** - discovered opportunities others missed
- **Better risk assessment** - avoided several investments that seemed good from single perspectives but had hidden flaws

**Understanding the M Parameter: Balancing Diversity and Efficiency**

The \`M\` parameter controls how many reasoning chains to generate. Choosing the right value involves trade-offs:

- **M=2-3**: Good for quick validation and basic perspective diversity
- **M=4-6**: Optimal for most complex decision-making scenarios
- **M=7+**: Useful for extremely high-stakes decisions, but with diminishing returns

**Temperature and Diversity Management**

Higher temperature settings (0.7-1.0) increase diversity between chains but may reduce individual chain quality. Lower settings (0.3-0.5) produce more consistent reasoning but less perspective diversity. \`MultiChainComparison\` typically works best with moderate to high temperature settings.

**The Art of Synthesis**

The most sophisticated aspect of \`MultiChainComparison\` is how it synthesizes multiple perspectives into a coherent final output. This isn't simple averaging - it's intelligent integration that:

- **Identifies common themes** across reasoning chains
- **Highlights significant disagreements** that warrant attention
- **Weighs different perspectives** based on confidence and consistency
- **Produces nuanced conclusions** that incorporate multiple viewpoints

**Designing Effective MultiChain Systems**

**Signature Design Tips:**
- Create outputs that naturally accommodate different perspectives
- Use broad, open-ended output fields that allow for diverse reasoning
- Include confidence or certainty measures to help with synthesis

**Quality Assurance:**
- Review reasoning chains individually to ensure each provides valuable perspective
- Look for pathological cases where all chains converge to identical reasoning
- Monitor for chains that produce consistently poor reasoning

**Performance Optimization:**
- Balance M parameter with computational budget
- Use caching for repeated similar queries
- Consider running chains in parallel for faster processing

\`\`\`python
# Compare multiple reasoning approaches
class AnalysisTask(dspy.Signature):
    """Analyze a business scenario from multiple perspectives."""
    
    scenario: str = dspy.InputField()
    
    financial_analysis: str = dspy.OutputField()
    risk_assessment: str = dspy.OutputField()
    recommendation: str = dspy.OutputField()

multi_analyzer = dspy.MultiChainComparison(
    AnalysisTask,
    M=3,  # Generate 3 different reasoning chains
    temperature=0.9  # High temperature for diversity
)

result = multi_analyzer(
    scenario="Company considering expansion to European market"
)

# Access multiple perspectives
print("=== Financial Perspectives ===")
for i, analysis in enumerate(result.completions):
    print(f"\\nPerspective {i+1}:")
    print(f"Financial: {analysis.financial_analysis}")
    print(f"Risk: {analysis.risk_assessment}")
    print(f"Recommendation: {analysis.recommendation}")

# The module automatically selects the best one
print(f"\\n=== Selected Best Analysis ===")
print(f"Recommendation: {result.recommendation}")
\`\`\`

## 🔗 Composing Modules into Programs: The Art of AI Architecture

Building with DSPy modules is like conducting a symphony orchestra. Each module is a skilled musician with their own expertise, but the magic happens when you bring them together in harmony. The conductor (that's you!) decides when the violins come in, when the drums build tension, and how all the instruments work together to create something beautiful.

This is the true power of DSPy - not just having smart components, but orchestrating them into intelligent systems that can tackle complex, real-world problems. Just as a symphony is more than the sum of its parts, a well-composed DSPy program can accomplish far more than any individual module could alone.

### The Philosophy of Modular Intelligence

Think about how humans solve complex problems. We don't just have one type of thinking - we have intuitive leaps, careful analysis, creative brainstorming, and practical execution. We switch between these modes naturally, and sometimes we even engage multiple types of thinking simultaneously.

DSPy modules mirror this human cognitive diversity. By composing different modules, you're creating an AI system that can think in multiple ways, just like humans do. A customer service system might use quick classification for simple requests, careful reasoning for complex problems, and tool-using agents for actions that require external information.

### Creating Custom DSPy Modules: Building Your Own Cognitive Components

Creating a custom DSPy module is like training a specialist for your team. You define their role, give them the tools they need, and teach them how to work with others. Here's how you build these cognitive specialists:

\`\`\`python
class MultiHopQA(dspy.Module):
    """A question-answering system that can handle multi-hop reasoning."""
    
    def __init__(self, k=3):
        self.retrieve = dspy.Retrieve(k=k)
        self.question_decomposer = dspy.ChainOfThought(
            "question -> subquestions: list[str]"
        )
        self.answer_synthesizer = dspy.ChainOfThought(
            "question, contexts -> answer"
        )
    
    def forward(self, question):
        # Decompose complex question into sub-questions
        decomposition = self.question_decomposer(question=question)
        
        # Retrieve context for each sub-question
        all_contexts = []
        for subq in decomposition.subquestions:
            contexts = self.retrieve(subq).passages
            all_contexts.extend(contexts)
        
        # Synthesize final answer
        return self.answer_synthesizer(
            question=question, 
            contexts=all_contexts
        )
\`\`\`

### The Art of Module Composition: Beyond Simple Chains

Module composition is where DSPy transforms from a collection of smart tools into intelligent systems. It's the difference between having a toolbox and being a master craftsperson who knows exactly which tool to use when, and how to combine them for complex projects.

**The Four Pillars of Effective Composition**

1. **Cohesion**: Each module should have a clear, focused responsibility
2. **Coupling**: Modules should be loosely coupled but effectively coordinated
3. **Flow**: Data and control should flow logically through your system
4. **Resilience**: Your composition should handle errors gracefully and provide fallbacks

**Understanding Composition Patterns**

Just as architects use proven patterns (Gothic arches, flying buttresses) to build cathedrals, DSPy developers use proven composition patterns to build robust AI systems. Each pattern solves common problems and provides a template for thinking about system architecture.

#### Composition Pattern 1: Sequential Pipeline - The Assembly Line

Sequential pipelines are like assembly lines in manufacturing - each station does one thing well, and the product moves systematically from start to finish. This pattern is perfect when your problem naturally breaks into distinct, ordered stages.

**When to Use Sequential Pipelines:**
- Document processing (extract → analyze → summarize → format)
- Data analysis workflows (collect → clean → analyze → report)
- Content creation (research → outline → write → edit)
- Customer service automation (classify → route → respond → follow-up)

**Key Success Factors:**
- Each stage should transform the data in a meaningful way
- Stages should be loosely coupled (one stage's implementation can change without affecting others)
- Include validation between stages to catch errors early
- Design stages to be testable independently

**Real-World Example: Financial Report Generator**

A financial services company needed to automatically generate investment reports from raw market data. Here's how they used a sequential pipeline:

\`\`\`python
class ResearchAssistant(dspy.Module):
    """Multi-stage research pipeline."""
    
    def __init__(self, num_sources=5):
        # Stage 1: Understand the query
        self.understand_query = dspy.ChainOfThought(
            "query -> search_terms: list[str], query_type: str"
        )
        
        # Stage 2: Search for information
        self.search = dspy.Retrieve(k=num_sources)
        
        # Stage 3: Evaluate source quality
        self.evaluate_sources = dspy.Predict(
            "source: str -> relevance: float, credibility: float"
        )
        
        # Stage 4: Synthesize information
        self.synthesize = dspy.ChainOfThought(
            "query, sources: list[str] -> summary: str, citations: list[str], confidence: float"
        )
    
    def forward(self, query):
        # Understand what we're looking for
        understanding = self.understand_query(query=query)
        
        # Search using extracted terms
        all_sources = []
        for term in understanding.search_terms:
            sources = self.search(term).passages
            all_sources.extend(sources)
        
        # Evaluate and filter sources
        evaluated_sources = []
        for source in all_sources[:10]:  # Limit to top 10
            eval_result = self.evaluate_sources(source=source)
            if eval_result.relevance > 0.7 and eval_result.credibility > 0.6:
                evaluated_sources.append(source)
        
        # Synthesize final answer
        return self.synthesize(
            query=query,
            sources=evaluated_sources
        )
\`\`\`

#### Composition Pattern 2: Branching Logic - The Smart Router

Branching logic is like having a smart receptionist who knows exactly which department to route each visitor to based on their needs. This pattern is essential when different types of inputs require fundamentally different processing approaches.

**The Intelligence of Routing**

The power of branching logic lies in its ability to provide specialized processing while maintaining a unified interface. Users interact with one system, but behind the scenes, their requests are intelligently routed to the most appropriate handler.

**When Branching Logic is Essential:**
- **Multi-domain systems**: Customer service handling technical, billing, and general inquiries
- **Content platforms**: Processing text, images, videos, and audio differently
- **Risk assessment**: Different evaluation criteria for different types of applications
- **Educational systems**: Adapting content delivery based on learning styles or skill levels

**Key Design Principles:**
- Make routing decisions based on clear, testable criteria
- Ensure consistent output formats across all branches
- Include fallback handling for edge cases
- Design branches to be independently testable and maintainable

**Avoiding Common Pitfalls:**
- Don't create too many branches (usually 3-7 is optimal)
- Avoid deep nesting of conditional logic
- Include monitoring to track which branches are used most
- Plan for easy addition of new branches as requirements evolve

**Case Study: Medical Triage System**

A healthcare provider needed an AI system to triage patient inquiries, routing them to appropriate care levels. Different symptoms required different assessment approaches:

\`\`\`python
class AdaptiveCustomerSupport(dspy.Module):
    """Route customer queries to specialized handlers."""
    
    def __init__(self):
        # Query classifier
        self.classifier = dspy.Predict(
            "query -> category: Literal['technical', 'billing', 'general', 'complaint']"
        )
        
        # Specialized handlers
        self.technical_support = dspy.ChainOfThought(
            "issue: str, system_info: str -> solution: str, steps: list[str]"
        )
        
        self.billing_support = dspy.ReAct(
            "issue: str, account_id: str -> resolution: str, actions_taken: list[str]",
            tools=[self.lookup_account, self.process_refund, self.update_billing]
        )
        
        self.complaint_handler = dspy.ChainOfThought(
            "complaint: str -> acknowledgment: str, resolution: str, follow_up: str"
        )
        
        self.general_qa = dspy.Predict("question -> answer")
    
    def forward(self, query: str, context: dict = None):
        # Classify the query
        category = self.classifier(query=query).category
        
        # Route to appropriate handler
        if category == 'technical':
            return self.technical_support(
                issue=query,
                system_info=context.get('system_info', 'Not provided')
            )
        elif category == 'billing':
            return self.billing_support(
                issue=query,
                account_id=context.get('account_id', 'Unknown')
            )
        elif category == 'complaint':
            result = self.complaint_handler(complaint=query)
            # Log complaints for review
            self.log_complaint(query, result)
            return result
        else:
            return self.general_qa(question=query)
    
    def lookup_account(self, account_id: str) -> str:
        return f"Account {account_id} details: Active, Balance: " + "$0"
    
    def process_refund(self, amount: float, reason: str) -> str:
        return f"Refund of $" + f"{amount} processed for: {reason}"
    
    def update_billing(self, account_id: str, changes: str) -> str:
        return f"Billing updated for {account_id}: {changes}"
    
    def log_complaint(self, complaint: str, resolution: dict):
        # In practice, save to database
        print(f"Complaint logged: {complaint[:50]}...")
\`\`\`

#### Composition Pattern 3: Ensemble Methods

\`\`\`python
class RobustAnalyzer(dspy.Module):
    """Use multiple approaches and combine results."""
    
    def __init__(self):
        # Different analysis approaches
        self.fast_analyzer = dspy.Predict(
            "text -> sentiment: str, key_points: list[str]"
        )
        
        self.deep_analyzer = dspy.ChainOfThought(
            "text -> sentiment: str, key_points: list[str], nuances: str"
        )
        
        self.comparative_analyzer = dspy.MultiChainComparison(
            "text -> sentiment: str, key_points: list[str], confidence: float",
            M=3
        )
        
        # Result combiner
        self.combiner = dspy.ChainOfThought(
            """analyses: list[dict] -> 
            final_sentiment: str, 
            consensus_points: list[str], 
            confidence: float,
            disagreements: list[str]"""
        )
    
    def forward(self, text: str):
        # Run all analyzers in parallel
        results = []
        
        # Fast analysis
        fast = self.fast_analyzer(text=text)
        results.append({
            'method': 'fast',
            'sentiment': fast.sentiment,
            'key_points': fast.key_points
        })
        
        # Deep analysis
        deep = self.deep_analyzer(text=text)
        results.append({
            'method': 'deep',
            'sentiment': deep.sentiment,
            'key_points': deep.key_points,
            'nuances': deep.nuances
        })
        
        # Comparative analysis
        comp = self.comparative_analyzer(text=text)
        results.append({
            'method': 'comparative',
            'sentiment': comp.sentiment,
            'key_points': comp.key_points,
            'confidence': comp.confidence
        })
        
        # Combine results
        return self.combiner(analyses=results)
\`\`\`

#### Composition Pattern 4: State Management

\`\`\`python
class StatefulConversationAgent(dspy.Module):
    """Maintain conversation state across interactions."""
    
    def __init__(self):
        self.conversation_history = []
        self.user_profile = {}
        
        # Components
        self.intent_classifier = dspy.Predict(
            "message: str, history: list[str] -> intent: str, entities: dict"
        )
        
        self.response_generator = dspy.ChainOfThought(
            """message: str, 
            intent: str, 
            entities: dict, 
            history: list[str], 
            user_profile: dict -> 
            response: str, 
            update_profile: dict,
            follow_up_needed: bool"""
        )
        
        self.profile_updater = dspy.Predict(
            "current_profile: dict, updates: dict -> new_profile: dict"
        )
    
    def forward(self, message: str):
        # Classify intent with history context
        intent_result = self.intent_classifier(
            message=message,
            history=self.conversation_history[-5:]  # Last 5 messages
        )
        
        # Generate response
        response_result = self.response_generator(
            message=message,
            intent=intent_result.intent,
            entities=intent_result.entities,
            history=self.conversation_history,
            user_profile=self.user_profile
        )
        
        # Update conversation history
        self.conversation_history.append(f"User: {message}")
        self.conversation_history.append(f"Assistant: {response_result.response}")
        
        # Update user profile if needed
        if response_result.update_profile:
            profile_result = self.profile_updater(
                current_profile=self.user_profile,
                updates=response_result.update_profile
            )
            self.user_profile = profile_result.new_profile
        
        return dspy.Prediction(
            response=response_result.response,
            follow_up_needed=response_result.follow_up_needed,
            conversation_length=len(self.conversation_history)
        )
    
    def reset_conversation(self):
        """Start a new conversation while keeping user profile."""
        self.conversation_history = []
    
    def get_conversation_summary(self):
        """Generate a summary of the conversation."""
        summarizer = dspy.ChainOfThought(
            "conversation: list[str] -> summary: str, key_topics: list[str]"
        )
        return summarizer(conversation=self.conversation_history)

## 🔍 Troubleshooting and Best Practices

### Common Pitfalls and How to Avoid Them

Even experienced developers can fall into common traps when building DSPy systems. Here are the most frequent issues and how to solve them:

**1. The "Everything in One Signature" Trap**

*Problem*: Trying to cram too many responsibilities into a single signature.
\`\`\`python
# BAD: Too many unrelated tasks
class DoEverything(dspy.Signature):
    text: str = dspy.InputField()
    sentiment: str = dspy.OutputField()
    summary: str = dspy.OutputField()
    translation: str = dspy.OutputField()
    keywords: list = dspy.OutputField()
    category: str = dspy.OutputField()
\`\`\`

*Solution*: Break complex tasks into focused, single-responsibility signatures.
\`\`\`python
# BETTER: Focused signatures
class AnalyzeSentiment(dspy.Signature):
    text: str = dspy.InputField()
    sentiment: str = dspy.OutputField()

class SummarizeText(dspy.Signature):
    text: str = dspy.InputField()
    summary: str = dspy.OutputField()
\`\`\`

**2. The "Vague Description" Problem**

*Problem*: Field descriptions that don't provide enough context.
\`\`\`python
# BAD: Vague descriptions
class BadSignature(dspy.Signature):
    data: str = dspy.InputField(desc="Some data")
    result: str = dspy.OutputField(desc="The result")
\`\`\`

*Solution*: Provide specific, actionable descriptions that include context and constraints.
\`\`\`python
# BETTER: Specific, contextual descriptions
class GoodSignature(dspy.Signature):
    customer_email: str = dspy.InputField(
        desc="Customer service email including original message and any quoted replies"
    )
    response_category: str = dspy.OutputField(
        desc="Response category: technical_support, billing_inquiry, or general_question"
    )
\`\`\`

**3. The "Ignore Error Handling" Mistake**

*Problem*: Not planning for what happens when modules fail or produce unexpected outputs.

*Solution*: Always include error handling and validation in your compositions.
\`\`\`python
class RobustModule(dspy.Module):
    def forward(self, input_data):
        try:
            result = self.process(input_data)
            
            # Validate output format
            if not self.validate_output(result):
                return self.fallback_response(input_data)
                
            return result
        except Exception as e:
            logger.error(f"Module failed: {e}")
            return self.error_response(input_data, e)
\`\`\`

### Performance Optimization Tips

**1. Choose the Right Module for the Job**
- Use \`Predict\` for simple, fast tasks
- Use \`ChainOfThought\` only when reasoning is truly needed
- Use \`ReAct\` sparingly - it's powerful but expensive

**2. Optimize Token Usage**
- Keep input contexts concise but informative
- Use \`max_len\` parameters to control output length
- Consider summarizing long contexts before processing

**3. Leverage Caching**
- Cache results for repeated queries
- Use deterministic signatures for better cache hit rates
- Consider preprocessing to normalize inputs

### Debugging DSPy Programs

**Step 1: Isolate the Problem**
\`\`\`python
# Test individual components
component = MyModule()
test_input = {...}
result = component(test_input)
print(f"Output: {result}")
print(f"Type: {type(result)}")
\`\`\`

**Step 2: Add Logging**
\`\`\`python
import logging

class DebuggableModule(dspy.Module):
    def forward(self, input_data):
        logging.info(f"Input received: {input_data}")
        
        result = self.process(input_data)
        
        logging.info(f"Output generated: {result}")
        return result
\`\`\`

**Step 3: Validate Assumptions**
\`\`\`python
# Check signature parsing
sig = MySignature()
print(f"Input fields: {sig.input_fields}")
print(f"Output fields: {sig.output_fields}")

# Test with minimal examples
minimal_input = {"field": "simple test"}
result = module(minimal_input)
\`\`\`

### Monitoring Production DSPy Systems

**Key Metrics to Track:**
- Response time per module
- Token usage and costs
- Error rates by module type
- Output quality scores
- User satisfaction metrics

**Setting Up Monitoring:**
\`\`\`python
import time
from datetime import datetime

class MonitoredModule(dspy.Module):
    def forward(self, input_data):
        start_time = time.time()
        
        try:
            result = self.process(input_data)
            
            # Log success metrics
            self.log_success({
                'duration': time.time() - start_time,
                'input_size': len(str(input_data)),
                'output_size': len(str(result)),
                'timestamp': datetime.now()
            })
            
            return result
            
        except Exception as e:
            # Log error metrics
            self.log_error({
                'error': str(e),
                'duration': time.time() - start_time,
                'timestamp': datetime.now()
            })
            raise
\`\`\`

### Decision-Making Framework: Choosing the Right Approach

**The DSPy Decision Tree**

When facing a new problem, use this systematic approach to choose the right DSPy components:

**Step 1: Understand Your Problem**
- Is it a single-step task or multi-step process?
- Do you need to explain the reasoning?
- Does it require external information or tools?
- How important is speed vs accuracy?
- What's your tolerance for errors?

**Step 2: Choose Your Core Pattern**

*For Single-Step Tasks:*
- **Simple classification/extraction** → \`dspy.Predict\`
- **Need reasoning transparency** → \`dspy.ChainOfThought\`
- **Require external tools** → \`dspy.ReAct\`
- **Want multiple perspectives** → \`dspy.MultiChainComparison\`

*For Multi-Step Processes:*
- **Linear workflow** → Sequential Pipeline
- **Different handling by type** → Branching Logic
- **Need consensus/robustness** → Ensemble Methods
- **Complex reasoning chains** → Recursive Processing

**Step 3: Design Your Signatures**

*Start Simple:*
\`\`\`python
# Prototype quickly
simple = dspy.Predict("input -> output")
\`\`\`

*Add Structure:*
\`\`\`python
# Add types and constraints
typed = dspy.Predict("text: str -> category: str, confidence: float")
\`\`\`

*Go Professional:*
\`\`\`python
# Full class-based signature with documentation
class ProductionSignature(dspy.Signature):
    """Detailed docstring explaining the task context and expectations."""
    field: type = dspy.InputField(desc="Specific description")
\`\`\`

**Step 4: Implement and Test**

*Build Incrementally:*
1. Start with the simplest version that works
2. Add complexity only when needed
3. Test each component independently
4. Integrate and test the full system

*Validate Thoroughly:*
- Test with edge cases
- Verify error handling
- Check performance under load
- Validate output quality

### When NOT to Use DSPy

DSPy is powerful, but it's not always the right choice:

**Consider alternatives when:**
- **Simple regex/rule-based logic works**: Don't use AI for problems that deterministic rules solve reliably
- **Real-time, low-latency requirements**: Traditional ML models might be faster
- **Highly sensitive applications**: Where explainability requirements exceed what current LLMs can provide
- **Very limited data**: When you have insufficient examples for optimization
- **Extremely cost-sensitive scenarios**: Where the token costs outweigh the benefits

### Architecture Patterns for Different Domains

**Customer Service Systems:**
\`\`\`
User Input → Classifier → Router → {
    Technical: ChainOfThought + Tools
    Billing: ReAct + Database
    General: Predict + Knowledge Base
} → Response Generator
\`\`\`

**Content Analysis Platforms:**
\`\`\`
Content → Format Detector → {
    Text: Sequential Pipeline (extract → analyze → categorize)
    Images: Multi-modal Signature
    Documents: Recursive Summarizer
} → Unified Output
\`\`\`

**Research and Analysis Tools:**
\`\`\`
Query → Query Expander → Multi-hop Retriever → 
Evidence Evaluator → Synthesis Module → Citation Generator
\`\`\`

## 🛠️ Working with Tools and Retrievers: Extending AI Beyond Pure Reasoning

Imagine if humans could only think but never act - we'd be like brilliant philosophers trapped in glass boxes, full of insights but unable to interact with the world. This is exactly the limitation that traditional language models face. They can reason brilliantly about abstract concepts, but they can't look up current information, perform calculations, or take actions in the real world.

DSPy's tools and retrievers change everything. They give your AI systems hands to work with, eyes to see new information, and legs to walk into databases and APIs. Suddenly, your AI doesn't just think - it can research, calculate, search, and act. It becomes a true digital colleague rather than just a reasoning engine.

### The RAG Revolution: Teaching AI to Research

Retrieval-Augmented Generation (RAG) is perhaps one of the most practical breakthroughs in modern AI. Think of it as giving your AI system access to a vast digital library with an expert librarian. When faced with a question, the AI doesn't just rely on its training data (which might be outdated or incomplete) - it actively searches for current, relevant information and uses that to inform its response.

This transforms AI from a closed-book test-taker into an open-book researcher. Just like how students perform better when they can reference materials while working on complex problems, AI systems with RAG capabilities can provide more accurate, up-to-date, and comprehensive answers.

### Building RAG Systems

\`\`\`python
# Configure retriever
colbert = dspy.ColBERTv2(url='http://20.102.90.50:2017/wiki17_abstracts')
dspy.settings.configure(rm=colbert)

# Basic RAG
class SimpleRAG(dspy.Module):
    def __init__(self, k=5):
        self.retrieve = dspy.Retrieve(k=k)
        self.generate = dspy.ChainOfThought("context, question -> answer")
    
    def forward(self, question):
        passages = self.retrieve(question).passages
        return self.generate(context=passages, question=question)

# Advanced RAG with reranking
class AdvancedRAG(dspy.Module):
    def __init__(self, initial_k=10, final_k=3):
        self.initial_k = initial_k
        self.final_k = final_k
        
        # Retrieval
        self.retrieve = dspy.Retrieve(k=initial_k)
        
        # Reranker
        self.rerank = dspy.ChainOfThought(
            "question: str, passage: str -> relevance_score: float, reasoning: str"
        )
        
        # Generator with citations
        self.generate = dspy.ChainOfThought(
            """question: str, 
            passages: list[str] -> 
            answer: str, 
            citations: list[int],
            confidence: float"""
        )
    
    def forward(self, question):
        # Initial retrieval
        initial_passages = self.retrieve(question).passages
        
        # Rerank passages
        ranked_passages = []
        for i, passage in enumerate(initial_passages):
            score_result = self.rerank(question=question, passage=passage)
            ranked_passages.append({
                'passage': passage,
                'score': score_result.relevance_score,
                'reasoning': score_result.reasoning,
                'index': i
            })
        
        # Sort by score and take top k
        ranked_passages.sort(key=lambda x: x['score'], reverse=True)
        top_passages = ranked_passages[:self.final_k]
        
        # Generate answer with citations
        passage_texts = [p['passage'] for p in top_passages]
        result = self.generate(question=question, passages=passage_texts)
        
        # Map citations back to original indices
        original_citations = [top_passages[i]['index'] for i in result.citations]
        
        return dspy.Prediction(
            answer=result.answer,
            citations=original_citations,
            confidence=result.confidence,
            reranking_reasoning=[p['reasoning'] for p in top_passages]
        )
\`\`\`

### Custom Tool Integration

\`\`\`python
from typing import Callable, List, Dict, Any
import requests
import json

# Tool wrapper for DSPy
class DSPyTool:
    def __init__(self, func: Callable, name: str = None, description: str = None):
        self.func = func
        self.name = name or func.__name__
        self.description = description or func.__doc__
    
    def __call__(self, *args, **kwargs):
        try:
            result = self.func(*args, **kwargs)
            return json.dumps(result) if isinstance(result, (dict, list)) else str(result)
        except Exception as e:
            return f"Error: {str(e)}"

# Create custom tools
@DSPyTool
def web_search(query: str, num_results: int = 5) -> List[Dict[str, str]]:
    """Search the web for information."""
    # In practice, use a real search API
    results = []
    for i in range(num_results):
        results.append({
            'title': f"Result {i+1} for {query}",
            'snippet': f"Information about {query}...",
            'url': f"https://example.com/{i}"
        })
    return results

@DSPyTool
def calculator(expression: str) -> float:
    """Evaluate mathematical expressions safely."""
    import ast
    import operator
    
    ops = {
        ast.Add: operator.add,
        ast.Sub: operator.sub,
        ast.Mult: operator.mul,
        ast.Div: operator.truediv,
        ast.Pow: operator.pow
    }
    
    def eval_expr(node):
        if isinstance(node, ast.Num):
            return node.n
        elif isinstance(node, ast.BinOp):
            return ops[type(node.op)](eval_expr(node.left), eval_expr(node.right))
        elif isinstance(node, ast.UnaryOp):
            return ops[type(node.op)](eval_expr(node.operand))
        else:
            raise TypeError(node)
    
    return eval_expr(ast.parse(expression, mode='eval').body)

@DSPyTool
def database_query(sql: str, table: str = "customers") -> List[Dict]:
    """Execute SQL queries on the database."""
    # Simulated database
    if "SELECT" in sql.upper():
        return [
            {"id": 1, "name": "Alice", "purchases": 5},
            {"id": 2, "name": "Bob", "purchases": 3}
        ]
    return []

# Create an agent with custom tools
class ResearchAgent(dspy.Module):
    def __init__(self):
        self.tools = [web_search, calculator, database_query]
        
        self.planner = dspy.ChainOfThought(
            """task: str -> 
            plan: list[str], 
            tools_needed: list[str],
            success_criteria: str"""
        )
        
        self.executor = dspy.ReAct(
            "task: str, plan: list[str] -> result: str, evidence: list[str]",
            tools=self.tools,
            max_iters=10
        )
        
        self.validator = dspy.ChainOfThought(
            """result: str, 
            evidence: list[str], 
            success_criteria: str -> 
            is_valid: bool, 
            feedback: str"""
        )
    
    def forward(self, task: str):
        # Plan the approach
        plan_result = self.planner(task=task)
        
        # Execute the plan
        exec_result = self.executor(
            task=task,
            plan=plan_result.plan
        )
        
        # Validate results
        validation = self.validator(
            result=exec_result.result,
            evidence=exec_result.evidence,
            success_criteria=plan_result.success_criteria
        )
        
        if not validation.is_valid:
            # Retry with feedback
            refined_task = f"{task}\\n\\nPrevious attempt feedback: {validation.feedback}"
            exec_result = self.executor(task=refined_task, plan=plan_result.plan)
        
        return dspy.Prediction(
            result=exec_result.result,
            evidence=exec_result.evidence,
            plan=plan_result.plan,
            tools_used=[t for t in plan_result.tools_needed]
        )
\`\`\`

### Advanced Retriever Patterns

\`\`\`python
# Multi-hop retrieval
class MultiHopRetriever(dspy.Module):
    def __init__(self, max_hops=3, k_per_hop=3):
        self.max_hops = max_hops
        self.k_per_hop = k_per_hop
        
        self.retrieve = dspy.Retrieve(k=k_per_hop)
        
        self.extract_next_query = dspy.ChainOfThought(
            """original_query: str,
            current_passages: list[str],
            hop_number: int ->
            next_query: str,
            should_continue: bool,
            information_gained: str"""
        )
        
        self.synthesize = dspy.ChainOfThought(
            """original_query: str,
            all_passages: list[str],
            hop_information: list[str] ->
            final_answer: str,
            reasoning_path: list[str]"""
        )
    
    def forward(self, query: str):
        all_passages = []
        hop_information = []
        current_query = query
        
        for hop in range(self.max_hops):
            # Retrieve passages
            passages = self.retrieve(current_query).passages
            all_passages.extend(passages)
            
            # Determine next query
            next_result = self.extract_next_query(
                original_query=query,
                current_passages=passages,
                hop_number=hop + 1
            )
            
            hop_information.append(next_result.information_gained)
            
            if not next_result.should_continue:
                break
            
            current_query = next_result.next_query
        
        # Synthesize final answer
        return self.synthesize(
            original_query=query,
            all_passages=all_passages,
            hop_information=hop_information
        )
\`\`\`

## 📋 Module 2 Summary: From Components to Composer

Congratulations! You've just completed one of the most transformative modules in your DSPy journey. You started this module understanding basic DSPy concepts, and you're finishing it as someone who can architect sophisticated AI systems. This isn't just about memorizing APIs - you've developed the thinking patterns and design intuition that separate DSPy beginners from DSPy experts.

### The Symphony of Skills You've Mastered

Think of what you've accomplished as learning to be a conductor of an AI orchestra. You now understand each instrument (module) intimately, know how to write the sheet music (signatures) that guides their performance, and can compose complex pieces (programs) that bring everything together harmoniously.

✅ **Signatures: Your AI Communication Mastery**
You've evolved from simple string-based specifications to crafting sophisticated, type-safe interfaces that guide AI behavior with precision. You understand the spectrum from quick inline signatures to comprehensive class-based specifications, and you know when each approach serves you best. You can now create dynamic signatures, use inheritance patterns, and design multi-modal interfaces that handle complex, real-world data types.

✅ **Core Modules: Your Cognitive Toolkit Mastery**
You've gone beyond knowing what each module does to understanding their personalities and use cases. You can now choose the right tool for each job: Predict for efficiency, ChainOfThought for transparency, ProgramOfThought for computational tasks, ReAct for complex agent behavior, and MultiChainComparison for robust decision-making. Each module is now a trusted colleague in your AI development toolkit.

✅ **Composition Patterns: Your Architecture Expertise**
You've learned the fundamental patterns that turn simple components into powerful systems. Sequential pipelines for step-by-step processing, branching logic for adaptive behavior, ensemble methods for robust results, recursive patterns for complex reasoning, and state management for conversational systems. These aren't just code patterns - they're thinking patterns that will guide how you approach any complex AI problem.

✅ **Tools and Retrievers: Your Real-World Integration Skills**
You've transformed from building AI systems that only think to creating systems that can act in the real world. You understand RAG architectures that can research and learn, tool integration that extends AI capabilities, and advanced retrieval patterns that can perform multi-hop reasoning across vast knowledge bases. Your AI systems are no longer isolated thinkers - they're connected, capable agents.

### Quick Reference

\`\`\`python
# Signature patterns
"input -> output"                          # Simple
"text: str -> summary: str, tags: list"   # Typed
class Custom(dspy.Signature): ...          # Class-based

# Module usage
dspy.Predict(signature)                    # Basic
dspy.ChainOfThought(signature)            # With reasoning
dspy.ProgramOfThought(signature)          # Code generation
dspy.ReAct(signature, tools=[...])        # With tools
dspy.MultiChainComparison(signature, M=3) # Ensemble

# Composition template
class MyProgram(dspy.Module):
    def __init__(self):
        self.component1 = dspy.Predict(...)
        self.component2 = dspy.ChainOfThought(...)
    
    def forward(self, **inputs):
        # Compose components
        result1 = self.component1(**inputs)
        result2 = self.component2(input=result1.output)
        return result2
\`\`\`

### The Path Forward: From Architect to Optimizer

You've now mastered the fundamental building blocks of DSPy programming. You can create sophisticated signatures, orchestrate multiple modules into intelligent systems, and build programs that can both reason and act in the real world. But there's one crucial skill that separates good DSPy developers from great ones: the ability to systematically improve and optimize your systems.

In the real world, your first version of a DSPy program is rarely your final version. Just as software engineers continuously refine their code, and data scientists iterate on their models, DSPy developers must learn to evaluate, measure, and optimize their programs. This is where DSPy truly shines - its ability to automatically improve your systems based on data and feedback.

### What Awaits in Module 3: The Science of AI Improvement

Module 3 will transform you from someone who builds DSPy programs to someone who scientifically improves them:

- **Building Effective Datasets**: Learn how to curate and create datasets that reveal your system's true capabilities and limitations
- **Defining Meaningful Metrics**: Move beyond simple accuracy to metrics that capture what really matters for your specific use case
- **Running Systematic Evaluations**: Develop the discipline and tools to measure performance consistently and comprehensively
- **Iterative Improvement Strategies**: Master the art of using evaluation results to guide systematic improvements

### Your Journey Continues

You now have a solid foundation in DSPy programming - the tools, the patterns, and the thinking frameworks. You're ready to build systems that matter. But remember: building is just the beginning. The real magic happens when you learn to make those systems better, systematically and scientifically.

Time to learn how to measure and improve your programs. Time to become a DSPy optimizer!`,
  exercises: [
    {
      id: 'dspy-m2-ex1',
      title: 'Build a Smart Product Review Analyzer',
      description:
        'Create an advanced signature-based system that analyzes customer reviews with precision',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
You're working for an e-commerce company that receives thousands of product reviews daily. The customer insights team wants a sophisticated AI system that can analyze reviews to understand not just sentiment, but also predict ratings, extract key themes, and provide actionable summaries for product managers.

## Your Goal
Build a DSPy signature that can:
1. Analyze customer review text for multiple insights simultaneously
2. Predict sentiment (positive, negative, neutral)
3. Estimate what star rating the customer would give (1-5 stars)
4. Extract key themes and topics mentioned in the review
5. Generate a concise summary for business stakeholders

## Requirements
- Use a class-based signature with proper type annotations
- Include descriptive field documentation for all inputs and outputs
- Handle multiple input fields (review text and product category)
- Provide structured outputs that can be used by other systems
- Test with real-world review examples

## Test Reviews to Analyze
1. "I love this coffee maker! It brews perfect coffee every morning and the programmable timer is so convenient. The only minor issue is that it's a bit loud when grinding, but the quality makes up for it."
2. "Terrible product. Arrived broken and customer service was unhelpful. Waste of money."
3. "Decent headphones for the price. Sound quality is okay, nothing spectacular but they do the job."

## Starting Point
Think about what information a product manager would want from customer feedback. They need to understand not just if customers are happy, but what specifically they're happy or unhappy about, and how this translates to business metrics.`,
      hints: [
        'Start with the class definition: class ReviewAnalysis(dspy.Signature). The class name should be descriptive.',
        'Use proper docstrings - the triple quotes after the class definition help DSPy understand the task.',
        'Input fields use dspy.InputField() with desc parameter for descriptions that guide the AI.',
        'Output fields use dspy.OutputField() with desc parameter to specify what kind of output you want.',
        'For sentiment, consider using Literal["positive", "negative", "neutral"] for type safety.',
        'For key themes, use list[str] to get multiple themes as a list.',
        'Test your signature by creating an instance: analyzer = dspy.Predict(ReviewAnalysis)',
        'Remember that good signatures are like good API design - clear inputs, structured outputs.',
      ],
      solution: `import dspy
from typing import Literal

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

class ReviewAnalysis(dspy.Signature):
    """Analyze product reviews comprehensively to extract business insights."""
    
    review_text: str = dspy.InputField(desc="The customer review text to analyze")
    product_category: str = dspy.InputField(desc="Product category (e.g., electronics, home, clothing)")
    
    sentiment: Literal['positive', 'negative', 'neutral'] = dspy.OutputField(desc="Overall sentiment of the review")
    predicted_rating: int = dspy.OutputField(desc="Predicted star rating from 1-5 based on review content")
    key_themes: list[str] = dspy.OutputField(desc="Main themes, features, or issues mentioned in the review")
    summary: str = dspy.OutputField(desc="Brief business-focused summary of the review")

# Create the review analyzer
review_analyzer = dspy.Predict(ReviewAnalysis)

# Test reviews
test_reviews = [
    {
        "text": "I love this coffee maker! It brews perfect coffee every morning and the programmable timer is so convenient. The only minor issue is that it's a bit loud when grinding, but the quality makes up for it.",
        "category": "kitchen appliances"
    },
    {
        "text": "Terrible product. Arrived broken and customer service was unhelpful. Waste of money.",
        "category": "electronics"
    },
    {
        "text": "Decent headphones for the price. Sound quality is okay, nothing spectacular but they do the job.",
        "category": "electronics"
    }
]

print("Product Review Analysis Results\\n" + "="*50)

for i, review in enumerate(test_reviews, 1):
    print(f"\\nReview {i}: {review['text'][:50]}...")
    print("-" * 40)
    
    result = review_analyzer(
        review_text=review["text"],
        product_category=review["category"]
    )
    
    print(f"Sentiment: {result.sentiment}")
    print(f"Predicted Rating: {result.predicted_rating}/5 stars")
    print(f"Key Themes: {', '.join(result.key_themes)}")
    print(f"Summary: {result.summary}")
    print("\\n" + "="*50)`,
    },
    {
      id: 'dspy-m2-ex2',
      title: 'Build an AI Research Assistant with Reasoning and Tools',
      description:
        'Create a sophisticated research system that combines planning, tool use, and synthesis',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're building an AI research assistant for a consulting firm. The consultants need a system that can tackle complex business questions that require both strategic thinking and data gathering. The system should be able to plan research approaches, execute searches using various tools, and synthesize findings into actionable insights.

## Your Goal
Create a research assistant module that demonstrates the power of combining different DSPy components:
1. **Strategic Planning**: Use ChainOfThought to break down complex questions and plan research approaches
2. **Active Research**: Use ReAct to execute research with external tools and data sources
3. **Intelligent Synthesis**: Combine findings with reasoning to generate comprehensive conclusions

## Requirements
- Build a custom DSPy module that inherits from dspy.Module
- Implement a three-stage process: Plan → Execute → Synthesize
- Use ChainOfThought for planning and synthesis phases
- Use ReAct with custom tools for the execution phase
- Handle complex, multi-faceted research questions
- Provide confidence levels and identify limitations

## Test Research Questions
1. "What are the key factors driving the growth of remote work adoption in tech companies, and what challenges do they face?"
2. "How is artificial intelligence changing the healthcare industry, and what are the regulatory implications?"
3. "What are the main drivers of electric vehicle adoption, and what barriers still exist?"

## Why This Architecture Matters
This exercise teaches you to orchestrate different types of AI reasoning - strategic planning (ChainOfThought), action-oriented problem solving (ReAct), and analytical synthesis. This mirrors how human researchers actually work and is essential for building production-ready AI systems.`,
      hints: [
        'Start with the module structure: class ResearchAssistant(dspy.Module) and implement __init__ and forward methods.',
        'In __init__, create three separate components: planner (ChainOfThought), executor (ReAct), and synthesizer (ChainOfThought).',
        'For the planner, use a signature that takes a research question and outputs research_plan, key_concepts, and expected_sources.',
        'Define mock tools for ReAct: search_academic(), get_statistics(), search_news() - these simulate real data sources.',
        'The executor should use ReAct with your custom tools and take the planning outputs as inputs.',
        'For synthesis, combine the original question with findings and evidence to produce conclusions.',
        'In forward(), chain the three components: plan → execute → synthesize.',
        'Consider adding error handling and validation between stages for robustness.',
        'Test with questions that require both reasoning and external information gathering.',
      ],
      solution: `import dspy

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Define custom research tools (simulated)
def search_academic(query: str) -> str:
    """Search academic papers and research studies."""
    # Simulated academic search results
    results = {
        "remote work": "Found 15 papers: Remote work productivity increased 13% (Stanford, 2024). Key factors: flexibility, reduced commute stress, better work-life balance.",
        "artificial intelligence healthcare": "Found 23 studies: AI diagnostics show 95% accuracy in radiology (Nature, 2024). FDA approved 67 AI medical devices in 2023.",
        "electric vehicle": "Research shows: EV sales up 40% in 2023. MIT study: charging infrastructure main adoption barrier."
    }
    for key in results:
        if key.lower() in query.lower():
            return results[key]
    return f"Found 8 academic papers related to {query} with recent findings and statistics."

def get_market_data(topic: str) -> str:
    """Get market statistics and business data."""
    market_data = {
        "remote work": "Market size: $4.8B in 2023, projected $9.5B by 2028. 42% of US workforce now remote-capable.",
        "healthcare": "AI in healthcare market: $15.1B in 2023, growing 37% annually. 73% of hospitals using AI tools.",
        "electric vehicle": "Global EV market: $388B in 2023, expected $1.7T by 2030. Tesla leads with 23% market share."
    }
    for key in market_data:
        if key.lower() in topic.lower():
            return market_data[key]
    return f"Market data for {topic}: Significant growth trends with expanding adoption rates."

def search_industry_news(query: str) -> str:
    """Search recent industry news and trends."""
    news_results = {
        "remote work": "Recent news: Google mandates 3-day office return. Survey: 67% prefer hybrid. Productivity tools market booming.",
        "AI healthcare": "Latest: ChatGPT integration in Epic EHR. New FDA guidelines for AI diagnostics. Privacy concerns rising.",
        "electric vehicle": "Breaking: Tesla opens Supercharger network. GM invests $7B in EV production. EU bans gas cars by 2035."
    }
    for key in news_results:
        if key.lower() in query.lower():
            return news_results[key]
    return f"Recent industry news about {query}: Major developments and policy changes affecting the sector."

class ResearchAssistant(dspy.Module):
    def __init__(self):
        # Strategic planning component
        self.planner = dspy.ChainOfThought(
            """research_question: str -> 
            research_plan: list[str],
            key_concepts: list[str],
            data_sources_needed: list[str],
            expected_insights: str"""
        )
        
        # Active research execution with tools
        self.executor = dspy.ReAct(
            """research_plan: list[str], 
            key_concepts: list[str],
            data_sources: list[str] -> 
            academic_findings: str,
            market_insights: str,
            industry_trends: str,
            key_evidence: list[str]""",
            tools=[search_academic, get_market_data, search_industry_news],
            max_iters=6
        )
        
        # Intelligent synthesis component
        self.synthesizer = dspy.ChainOfThought(
            """original_question: str,
            research_plan: list[str],
            academic_findings: str,
            market_insights: str,
            industry_trends: str,
            evidence: list[str] ->
            executive_summary: str,
            key_findings: list[str],
            implications: list[str],
            confidence_level: str,
            limitations: list[str],
            recommendations: list[str]"""
        )
    
    def forward(self, research_question: str):
        print(f"🔍 Starting research on: {research_question}")
        print("=" * 60)
        
        # Stage 1: Strategic Planning
        print("\\n📋 PLANNING PHASE")
        print("-" * 30)
        plan_result = self.planner(research_question=research_question)
        
        print("Research Plan:")
        for i, step in enumerate(plan_result.research_plan, 1):
            print(f"  {i}. {step}")
        print(f"\\nKey Concepts: {', '.join(plan_result.key_concepts)}")
        
        # Stage 2: Active Research Execution
        print("\\n🔬 EXECUTION PHASE")
        print("-" * 30)
        execution_result = self.executor(
            research_plan=plan_result.research_plan,
            key_concepts=plan_result.key_concepts,
            data_sources=plan_result.data_sources_needed
        )
        
        print("Research completed using multiple data sources...")
        
        # Stage 3: Intelligent Synthesis
        print("\\n📊 SYNTHESIS PHASE")
        print("-" * 30)
        final_result = self.synthesizer(
            original_question=research_question,
            research_plan=plan_result.research_plan,
            academic_findings=execution_result.academic_findings,
            market_insights=execution_result.market_insights,
            industry_trends=execution_result.industry_trends,
            evidence=execution_result.key_evidence
        )
        
        return final_result

# Create and test the research assistant
research_assistant = ResearchAssistant()

# Test questions
test_questions = [
    "What are the key factors driving the growth of remote work adoption in tech companies, and what challenges do they face?",
    "How is artificial intelligence changing the healthcare industry, and what are the regulatory implications?",
]

print("AI Research Assistant Demo\\n" + "="*80)

for question in test_questions:
    result = research_assistant(research_question=question)
    
    print("\\n" + "="*60)
    print("FINAL RESEARCH REPORT")
    print("="*60)
    print(f"\\n📋 Executive Summary:\\n{result.executive_summary}")
    print(f"\\n🔍 Key Findings:")
    for finding in result.key_findings:
        print(f"  • {finding}")
    print(f"\\n💡 Implications:")
    for implication in result.implications:
        print(f"  • {implication}")
    print(f"\\n📈 Confidence Level: {result.confidence_level}")
    print(f"\\n⚠️  Limitations:")
    for limitation in result.limitations:
        print(f"  • {limitation}")
    print(f"\\n🎯 Recommendations:")
    for rec in result.recommendations:
        print(f"  • {rec}")
    print("\\n" + "="*80)`,
      testCases: [
        {
          input: 'dspy.ChainOfThought',
          expected: true,
          description: 'Uses ChainOfThought for planning',
        },
        {
          input: 'dspy.ReAct',
          expected: true,
          description: 'Uses ReAct for tool execution',
        },
      ],
    },
    {
      id: 'dspy-m2-ex3',
      title: 'Build a Smart Document Intelligence System',
      description:
        'Create a recursive AI system that can process and understand long, complex documents',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're working for a legal tech startup that processes lengthy contracts, research papers, and regulatory documents. Lawyers need to quickly understand 100+ page documents, but simple summarization often loses critical details or context. You need to build an intelligent system that can handle documents of any length while preserving important information.

## Your Goal
Create a sophisticated document intelligence system that uses recursive processing:
1. **Smart Chunking**: Intelligently break down long documents into meaningful sections
2. **Hierarchical Processing**: Process chunks at different levels of detail based on importance
3. **Recursive Summarization**: Summarize summaries until reaching the desired conciseness
4. **Information Preservation**: Maintain critical details throughout the compression process
5. **Adaptive Depth**: Automatically determine how deep to process based on content complexity

## Requirements
- Build a custom DSPy module that handles documents of varying lengths (1,000 to 50,000+ words)
- Implement recursive logic with proper base cases and depth control
- Use multiple DSPy components for chunking, summarization, and synthesis
- Preserve key information and important details at each recursion level
- Include importance scoring to prioritize content
- Handle edge cases (very short documents, very long documents, depth limits)

## Test Documents
1. **Short Report** (500 words): "Our quarterly sales analysis shows..."
2. **Medium Paper** (2,000 words): A research paper on AI ethics with multiple sections
3. **Long Contract** (8,000 words): A detailed software license agreement with many clauses

## Why Recursive Processing Matters
This exercise teaches you to build AI systems that can handle complexity through decomposition - a fundamental pattern in computer science and AI. Recursive processing allows you to tackle problems of arbitrary size while maintaining quality and preserving important information.`,
      hints: [
        'Start with class DocumentIntelligence(dspy.Module) and define __init__ with parameters like max_depth and chunk_size.',
        'Create three main components: a chunker (for splitting text), a summarizer (for processing chunks), and a synthesizer (for final assembly).',
        'The forward method should implement recursion with proper base cases: if text is short enough OR max depth reached, return direct summary.',
        'For the recursive case: chunk the text, process each chunk (potentially recursively), then synthesize results.',
        'Use importance scoring to decide which chunks deserve deeper processing - high importance chunks get recursive treatment.',
        'Include chunk_size as a parameter (e.g., 1000 characters) and max_depth (e.g., 3 levels) to prevent infinite recursion.',
        'Track processing depth and pass it through recursive calls: self.forward(chunk, depth + 1).',
        'The chunker should return both chunks and importance scores for intelligent processing.',
        'Test with documents of different lengths to see how recursion adapts to content size.',
        'Consider edge cases: what happens with empty text, single paragraph, or extremely long documents?',
      ],
      solution: `import dspy

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

class DocumentIntelligence(dspy.Module):
    def __init__(self, max_depth=3, chunk_size=1000, target_summary_length=200):
        self.max_depth = max_depth
        self.chunk_size = chunk_size
        self.target_summary_length = target_summary_length
        
        # Smart chunking component
        self.chunker = dspy.Predict(
            """document_text: str, 
            target_chunk_size: int -> 
            text_chunks: list[str],
            chunk_importance_scores: list[float],
            chunk_types: list[str]"""
        )
        
        # Adaptive summarization component
        self.summarizer = dspy.ChainOfThought(
            """text_content: str,
            importance_score: float,
            processing_depth: int,
            content_type: str ->
            summary: str,
            key_insights: list[str],
            critical_details: list[str],
            requires_deeper_analysis: bool"""
        )
        
        # Intelligent synthesis component
        self.synthesizer = dspy.ChainOfThought(
            """original_document_length: int,
            chunk_summaries: list[str],
            all_key_insights: list[str],
            all_critical_details: list[str],
            processing_depth: int ->
            comprehensive_summary: str,
            executive_overview: str,
            key_findings: list[str],
            important_details: list[str],
            document_structure: str"""
        )
    
    def forward(self, text: str, depth: int = 0):
        print(f"{'  ' * depth}📄 Processing at depth {depth} | Length: {len(text)} chars")
        
        # Base case: document is short enough OR max depth reached
        if len(text) <= self.chunk_size or depth >= self.max_depth:
            print(f"{'  ' * depth}✅ Base case reached - direct summarization")
            
            result = self.summarizer(
                text_content=text,
                importance_score=1.0,
                processing_depth=depth,
                content_type="final_processing"
            )
            
            return dspy.Prediction(
                summary=result.summary,
                key_insights=result.key_insights,
                critical_details=result.critical_details,
                depth=depth,
                chunks_processed=1,
                processing_path=f"Direct processing at depth {depth}"
            )
        
        # Recursive case: intelligent chunking and processing
        print(f"{'  ' * depth}🔄 Recursive case - chunking and processing")
        
        chunking_result = self.chunker(
            document_text=text,
            target_chunk_size=self.chunk_size
        )
        
        print(f"{'  ' * depth}📊 Created {len(chunking_result.text_chunks)} chunks")
        
        # Process each chunk with importance-based recursion
        chunk_summaries = []
        all_key_insights = []
        all_critical_details = []
        total_chunks_processed = 0
        processing_paths = []
        
        for i, (chunk, importance, chunk_type) in enumerate(zip(
            chunking_result.text_chunks, 
            chunking_result.chunk_importance_scores,
            chunking_result.chunk_types
        )):
            print(f"{'  ' * depth}  📝 Processing chunk {i+1}/{len(chunking_result.text_chunks)} | Importance: {importance:.2f} | Type: {chunk_type}")
            
            chunk_result = self.summarizer(
                text_content=chunk,
                importance_score=importance,
                processing_depth=depth,
                content_type=chunk_type
            )
            
            # Decide whether to recurse based on importance and content complexity
            if chunk_result.requires_deeper_analysis and importance > 0.7 and depth < self.max_depth - 1:
                print(f"{'  ' * depth}  🔍 High importance chunk - going deeper")
                
                # Recursive call for important, complex chunks
                recursive_result = self.forward(chunk, depth + 1)
                chunk_summaries.append(recursive_result.summary)
                all_key_insights.extend(recursive_result.key_insights)
                all_critical_details.extend(recursive_result.critical_details)
                total_chunks_processed += recursive_result.chunks_processed
                processing_paths.append(f"Recursive: {recursive_result.processing_path}")
            else:
                print(f"{'  ' * depth}  ✂️  Standard processing for chunk")
                
                # Standard processing for less critical chunks
                chunk_summaries.append(chunk_result.summary)
                all_key_insights.extend(chunk_result.key_insights)
                all_critical_details.extend(chunk_result.critical_details)
                total_chunks_processed += 1
                processing_paths.append(f"Direct processing at depth {depth}")
        
        # Intelligent synthesis of all processed chunks
        print(f"{'  ' * depth}🧠 Synthesizing {len(chunk_summaries)} summaries")
        
        final_result = self.synthesizer(
            original_document_length=len(text),
            chunk_summaries=chunk_summaries,
            all_key_insights=all_key_insights,
            all_critical_details=all_critical_details,
            processing_depth=depth
        )
        
        return dspy.Prediction(
            summary=final_result.comprehensive_summary,
            executive_overview=final_result.executive_overview,
            key_insights=final_result.key_findings,
            critical_details=final_result.important_details[:15],  # Top 15 most important
            document_structure=final_result.document_structure,
            depth=depth,
            chunks_processed=total_chunks_processed,
            processing_path=f"Processed {len(chunk_summaries)} chunks at depth {depth}"
        )

# Create the document intelligence system
document_ai = DocumentIntelligence(max_depth=3, chunk_size=800, target_summary_length=250)

# Test documents of different lengths
test_documents = [
    {
        "name": "Short Report",
        "content": """Our quarterly sales analysis shows significant growth in the cloud computing division. 
        Revenue increased by 23% compared to last quarter, primarily driven by enterprise clients adopting our 
        new AI-powered analytics platform. The marketing team's targeted campaign resulted in 156 new enterprise 
        leads, with a conversion rate of 34%. Customer satisfaction scores improved to 4.2/5, and our customer 
        support team reduced average response time to 2.3 hours. Looking ahead, we expect continued growth as 
        three major Fortune 500 companies are in final contract negotiations."""
    },
    {
        "name": "Medium Technical Paper",  
        "content": """Artificial Intelligence Ethics in Healthcare Applications: A Comprehensive Review
        
        Abstract: This paper examines the ethical implications of AI deployment in healthcare settings, 
        focusing on patient privacy, algorithmic bias, and decision transparency. We analyze 47 case studies 
        from major healthcare institutions and propose a framework for ethical AI implementation.
        
        Introduction: The integration of artificial intelligence in healthcare has accelerated dramatically, 
        with applications ranging from diagnostic imaging to treatment recommendation systems. However, this 
        rapid adoption raises significant ethical concerns that must be addressed systematically.
        
        Methodology: We conducted a systematic review of AI ethics literature from 2020-2024, analyzing 
        peer-reviewed papers, industry reports, and regulatory guidelines. Our analysis framework considered 
        four key dimensions: patient autonomy, beneficence, non-maleficence, and justice.
        
        Key Findings: Our research identified three critical areas requiring immediate attention: algorithmic 
        transparency in life-critical decisions, bias mitigation in diagnostic systems, and patient consent 
        frameworks for AI-assisted care. We found that 67% of surveyed healthcare AI systems lacked adequate 
        transparency mechanisms, and 34% showed measurable bias against minority populations.
        
        Conclusion: Healthcare AI ethics requires a multi-stakeholder approach involving clinicians, 
        technologists, ethicists, and patients. We recommend immediate implementation of our proposed 
        Ethical AI Healthcare Framework to ensure responsible innovation.""" * 3  # Make it longer
    }
]

print("Document Intelligence System Demo\\n" + "="*70)

for doc in test_documents:
    print(f"\\n{'='*70}")
    print(f"📋 ANALYZING: {doc['name'].upper()}")
    print(f"📏 Document Length: {len(doc['content'])} characters")
    print("="*70)
    
    result = document_ai(text=doc["content"])
    
    print(f"\\n📊 PROCESSING SUMMARY")
    print(f"Processing Depth: {result.depth} levels")
    print(f"Chunks Processed: {result.chunks_processed}")
    print(f"Processing Strategy: {result.processing_path}")
    
    print(f"\\n📄 EXECUTIVE OVERVIEW")
    print("-" * 40)
    print(result.executive_overview)
    
    print(f"\\n📝 COMPREHENSIVE SUMMARY")
    print("-" * 40)
    print(result.summary)
    
    print(f"\\n🔍 KEY INSIGHTS")
    print("-" * 40)
    for i, insight in enumerate(result.key_insights, 1):
        print(f"{i}. {insight}")
    
    print(f"\\n⚠️  CRITICAL DETAILS")
    print("-" * 40)
    for i, detail in enumerate(result.critical_details[:5], 1):  # Top 5
        print(f"{i}. {detail}")
    
    if hasattr(result, 'document_structure'):
        print(f"\\n🏗️  DOCUMENT STRUCTURE")
        print("-" * 40)
        print(result.document_structure)
    
    print("\\n" + "="*70)`,
      testCases: [
        {
          input: 'def forward(self, text: str, depth: int = 0)',
          expected: true,
          description: 'Recursive forward method with depth tracking',
        },
      ],
    },
    {
      id: 'dspy-m2-ex4',
      title: 'Build a Research-Grade Question Answering System',
      description:
        'Create an advanced QA system that performs multi-hop reasoning with full source attribution',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
You're building a QA system for a research university's digital library. Researchers need to ask complex questions that require connecting information from multiple sources, and they need full citation tracking for academic integrity. The system must be able to follow chains of reasoning across different documents and provide transparent source attribution.

## Your Goal
Create a sophisticated multi-hop QA system that:
1. **Multi-hop Reasoning**: Follows complex reasoning chains across multiple information sources (up to 3 hops)
2. **Source Tracking**: Maintains detailed provenance for every piece of information used
3. **Citation Generation**: Provides properly formatted citations for all sources
4. **Reasoning Transparency**: Shows the complete reasoning path taken to reach conclusions
5. **Quality Assessment**: Evaluates confidence levels for each reasoning step

## Requirements
- Build a custom DSPy module that can handle complex, multi-step questions
- Implement iterative retrieval and reasoning with proper source tracking
- Use multiple DSPy components for decomposition, retrieval, and synthesis
- Maintain a reasoning chain that shows how conclusions were reached
- Provide confidence scores and identify potential limitations
- Handle questions that require 1-3 reasoning hops

## Test Questions
1. "What are the environmental impacts of the technologies used in renewable energy production?"
2. "How do the economic policies discussed in recent papers affect small business growth in emerging markets?"
3. "What is the relationship between AI model size and energy consumption, and how does this impact sustainability goals?"

## Why Multi-hop Reasoning Matters
This exercise teaches you to build AI systems that can handle complex, real-world questions that require connecting disparate pieces of information - a crucial skill for research applications and knowledge work.`,
      hints: [
        'Start with class ResearchQA(dspy.Module) and define __init__ with max_hops parameter and retrieval component.',
        'Create components for: question decomposition, iterative retrieval, evidence synthesis, and citation formatting.',
        'Use dspy.Retrieve(k=5) for getting relevant documents at each reasoning hop.',
        'Implement a loop in forward() that performs retrieval -> reasoning -> question refinement for each hop.',
        'Track sources by maintaining a list of (source_id, content, reasoning_step) tuples throughout the process.',
        'The question decomposer should decide if more hops are needed and generate the next sub-question.',
        'Use ChainOfThought for evidence synthesis to combine information from multiple sources with reasoning.',
        'Create a citation formatter that generates proper academic citations from source metadata.',
        'Include confidence scoring for each reasoning step and overall answer quality.',
        'Test with questions that naturally require multiple information sources to answer completely.',
      ],
      solution: `import dspy

class MultiHopQAWithSources(dspy.Module):
    def __init__(self, max_hops=3):
        self.max_hops = max_hops
        
        # Retrieval component
        self.retrieve = dspy.Retrieve(k=5)
        
        # Question decomposer
        self.decomposer = dspy.ChainOfThought(
            """original_question: str,
            current_context: list[str],
            hop_number: int ->
            next_question: str,
            should_continue: bool,
            reasoning: str"""
        )
        
        # Answer extractor
        self.extractor = dspy.ChainOfThought(
            """question: str,
            passages: list[str] ->
            partial_answer: str,
            key_facts: list[str],
            source_indices: list[int]"""
        )
        
        # Final answer composer
        self.composer = dspy.ChainOfThought(
            """original_question: str,
            hop_answers: list[dict],
            all_sources: list[str] ->
            final_answer: str,
            citations: list[str],
            reasoning_path: list[str]"""
        )
    
    def forward(self, question: str):
        hop_answers = []
        all_sources = []
        current_question = question
        
        for hop in range(self.max_hops):
            # Retrieve relevant passages
            retrieval = self.retrieve(current_question)
            passages = retrieval.passages
            
            # Store sources with IDs
            source_start_idx = len(all_sources)
            all_sources.extend(passages)
            
            # Extract answer from current passages
            extraction = self.extractor(
                question=current_question,
                passages=passages
            )
            
            # Adjust source indices
            adjusted_indices = [
                idx + source_start_idx 
                for idx in extraction.source_indices
            ]
            
            hop_answers.append({
                'hop': hop + 1,
                'question': current_question,
                'answer': extraction.partial_answer,
                'facts': extraction.key_facts,
                'source_indices': adjusted_indices
            })
            
            # Determine next question
            if hop < self.max_hops - 1:
                decomposition = self.decomposer(
                    original_question=question,
                    current_context=extraction.key_facts,
                    hop_number=hop + 1
                )
                
                if not decomposition.should_continue:
                    break
                    
                current_question = decomposition.next_question
            else:
                break
        
        # Compose final answer with citations
        final_result = self.composer(
            original_question=question,
            hop_answers=hop_answers,
            all_sources=all_sources
        )
        
        # Format citations with proper academic style
        formatted_citations = []
        for i, citation in enumerate(final_result.citations):
            formatted_citations.append(f"[{i+1}] {citation}")
        
        return dspy.Prediction(
            answer=final_result.final_answer,
            citations=formatted_citations,
            reasoning_path=final_result.reasoning_path,
            num_hops=len(hop_answers),
            sources_used=len(set(sum([h['source_indices'] for h in hop_answers], []))),
            confidence_score=0.85,  # Would be calculated based on source quality
            research_depth=len(hop_answers)
        )

# Configure DSPy with retrieval
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Create the research QA system
research_qa = MultiHopQAWithSources(max_hops=3)

# Test questions requiring multi-hop reasoning
test_questions = [
    "What are the environmental impacts of the technologies used in renewable energy production?",
    "How do the economic policies discussed in recent papers affect small business growth in emerging markets?",
    "What is the relationship between AI model size and energy consumption, and how does this impact sustainability goals?"
]

print("Research-Grade QA System Demo\\n" + "="*80)

for i, question in enumerate(test_questions, 1):
    print(f"\\n{'='*80}")
    print(f"🔍 RESEARCH QUESTION {i}")
    print("="*80)
    print(f"Q: {question}")
    print("\\n" + "-"*80)
    
    # Note: In a real implementation, this would connect to actual retrieval systems
    print("⚠️  Note: This demo uses simulated retrieval. In production, connect to:")
    print("  • Academic databases (PubMed, arXiv, Google Scholar)")
    print("  • Document collections (PDFs, research papers)")
    print("  • Knowledge bases (Wikipedia, domain-specific sources)")
    
    # Simulated result structure
    print("\\n📊 REASONING PROCESS")
    print("-" * 40)
    print("Hop 1: Searching for renewable energy technologies...")
    print("  → Found: Solar panel manufacturing, wind turbine production")
    print("Hop 2: Searching for environmental impacts of these technologies...")
    print("  → Found: Mining impacts, lifecycle assessments, carbon footprints")
    print("Hop 3: Synthesizing comprehensive environmental impact analysis...")
    print("  → Combining evidence from multiple sources")
    
    print("\\n📝 ANSWER WITH CITATIONS")
    print("-" * 40)
    print("Renewable energy technologies have complex environmental impacts throughout")
    print("their lifecycle. Solar panel production requires mining of rare earth metals")
    print("[1], while wind turbines involve steel and concrete production [2]. However,")
    print("lifecycle assessments show net positive environmental benefits within 1-3 years")
    print("of operation [3]. The manufacturing phase creates 85% of total lifecycle emissions [4].")
    
    print("\\n📚 SOURCES")
    print("-" * 40)
    print("[1] Environmental Science & Technology, 2024: 'Rare Earth Mining for Solar Technology'")
    print("[2] Nature Energy, 2024: 'Wind Turbine Manufacturing Environmental Assessment'")
    print("[3] Journal of Cleaner Production, 2024: 'Renewable Energy Lifecycle Analysis'")
    print("[4] Energy Policy, 2024: 'Carbon Footprint of Renewable Energy Manufacturing'")
    
    print("\\n📈 RESEARCH METRICS")
    print("-" * 40)
    print(f"Reasoning Hops: 3")
    print(f"Sources Consulted: 12")
    print(f"Unique Sources Cited: 4")
    print(f"Confidence Score: 85%")
    print(f"Research Depth: Comprehensive")
    
    print("\\n" + "="*80)`,
      testCases: [
        {
          input: 'max_hops',
          expected: true,
          description: 'Implements multi-hop logic',
        },
        {
          input: 'source_indices',
          expected: true,
          description: 'Tracks source indices',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m2-quiz',
    title: 'Module 2: DSPy Programming Fundamentals - Knowledge Check',
    description: 'Test your understanding of DSPy programming concepts',
    passingScore: 7,
    questionsPerQuiz: 10,
    questions: [], // This will be populated from questionBank randomly
    questionBank: [
      // Signatures & Type Systems (Questions 1-25)
      {
        id: 'q1',
        question: 'What is the main advantage of class-based signatures over inline signatures?',
        choices: [
          'Better performance',
          'More detailed documentation and type safety',
          'Faster execution',
          'Less memory usage',
        ],
        correctAnswer: 1,
        explanation:
          'Class-based signatures allow for detailed field descriptions, better type annotations, and clearer documentation.',
      },
      {
        id: 'q2',
        question: 'Which signature format is correct in DSPy?',
        choices: ['"input: output"', '"input -> output"', '"input => output"', '"input | output"'],
        correctAnswer: 1,
        explanation: 'DSPy uses the arrow notation "input -> output" for signatures.',
      },
      {
        id: 'q3',
        question: 'What are the three layers of value provided by proper type annotations in DSPy?',
        choices: [
          'Performance, memory, and speed',
          'Code safety, documentation, and model guidance',
          'Syntax, semantics, and style',
          'Input, output, and processing',
        ],
        correctAnswer: 1,
        explanation:
          'Type annotations provide code safety for IDEs, documentation for developers, and guidance for language models.',
      },
      {
        id: 'q4',
        question: 'When should you use simple inline signatures?',
        choices: [
          'Only in production systems',
          'For prototyping and straightforward tasks',
          'Never, always use class-based',
          'Only for complex multi-step processes',
        ],
        correctAnswer: 1,
        explanation:
          'Simple inline signatures are perfect for prototyping and when tasks are genuinely straightforward.',
      },
      {
        id: 'q5',
        question: 'What makes Pydantic integration powerful in DSPy signatures?',
        choices: [
          'It makes code run faster',
          'It provides validation, serialization, and clear error messages',
          'It reduces memory usage',
          'It eliminates the need for type hints',
        ],
        correctAnswer: 1,
        explanation:
          'Pydantic provides validation, serialization, and clear error messages beyond simple type hints.',
      },
      {
        id: 'q6',
        question: 'How do you specify multiple inputs in a DSPy signature?',
        choices: [
          '"input1 + input2 -> output"',
          '"input1, input2 -> output"',
          '"input1 & input2 -> output"',
          '"input1; input2 -> output"',
        ],
        correctAnswer: 1,
        explanation: 'Multiple inputs are specified with commas: "input1, input2 -> output".',
      },
      {
        id: 'q7',
        question: 'What should good field descriptions in signatures include?',
        choices: [
          'Just the field name',
          'Context, constraints, and purpose',
          'Only data types',
          'Performance specifications',
        ],
        correctAnswer: 1,
        explanation:
          'Good descriptions provide context about the fields purpose, any constraints, and how it will be used.',
      },
      {
        id: 'q8',
        question: 'When would you use dynamic signatures?',
        choices: [
          'Never, they are too complex',
          'When you need to create signatures based on runtime requirements',
          'Only for debugging',
          'Only in development environments',
        ],
        correctAnswer: 1,
        explanation:
          'Dynamic signatures are useful when signature requirements change based on runtime conditions or user input.',
      },
      {
        id: 'q9',
        question: 'What is signature inheritance used for?',
        choices: [
          'Making code run faster',
          'Sharing common fields and extending functionality',
          'Reducing memory usage',
          'Improving security',
        ],
        correctAnswer: 1,
        explanation:
          'Signature inheritance allows sharing common fields across related signatures while adding specialized functionality.',
      },
      {
        id: 'q10',
        question: 'What is the "Everything in One Signature" trap?',
        choices: [
          'Using too many input fields',
          'Cramming unrelated responsibilities into a single signature',
          'Making signatures too complex to understand',
          'Using only class-based signatures',
        ],
        correctAnswer: 1,
        explanation:
          'This trap involves trying to handle too many unrelated tasks in one signature, reducing clarity and effectiveness.',
      },
      {
        id: 'q11',
        question: 'How should you handle optional inputs in DSPy signatures?',
        choices: [
          'Always make them required',
          'Use field descriptions and default values',
          'Put them in separate signatures',
          'Use special syntax markers',
        ],
        correctAnswer: 1,
        explanation:
          'Optional inputs are best handled through field descriptions and default values.',
      },
      {
        id: 'q12',
        question: 'What role do signatures play in DSPy optimization?',
        choices: [
          'They are ignored during optimization',
          'They guide the optimization process by defining the interface',
          'They slow down optimization',
          'They only matter for error handling',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures define the input/output structure that guides how DSPy optimizers improve the system.',
      },
      {
        id: 'q13',
        question: 'When should you use Union types in DSPy signatures?',
        choices: [
          'Never, they are too confusing',
          'When inputs or outputs can be one of several different types',
          'Only for debugging',
          'Only in production systems',
        ],
        correctAnswer: 1,
        explanation:
          'Union types are useful when a field legitimately needs to handle multiple different data types.',
      },
      {
        id: 'q14',
        question: 'What is the benefit of using Literal types in outputs?',
        choices: [
          'Better performance',
          'Constraining outputs to specific valid values',
          'Reduced memory usage',
          'Faster compilation',
        ],
        correctAnswer: 1,
        explanation:
          'Literal types ensure outputs are constrained to specific predefined values, improving reliability.',
      },
      {
        id: 'q15',
        question: 'How do multi-modal signatures differ from regular signatures?',
        choices: [
          'They are faster to execute',
          'They can handle different types of input like text, images, and audio',
          'They use different syntax',
          'They require special hardware',
        ],
        correctAnswer: 1,
        explanation:
          'Multi-modal signatures can process different types of media inputs beyond just text.',
      },
      {
        id: 'q16',
        question: 'What should you avoid in signature docstrings?',
        choices: [
          'Clear task descriptions',
          'Vague or overly generic descriptions',
          'Context about the problem domain',
          'Examples of expected behavior',
        ],
        correctAnswer: 1,
        explanation:
          'Vague descriptions like "Do stuff with data" provide no useful guidance to the language model.',
      },
      {
        id: 'q17',
        question: 'How do signatures help with model portability?',
        choices: [
          'They make models run faster',
          'They provide a consistent interface across different language models',
          'They reduce model size',
          'They eliminate the need for training',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures create a consistent interface that works across different language models without code changes.',
      },
      {
        id: 'q18',
        question: 'What happens when your data doesn\t match the signature specification?',
        choices: [
          'DSPy automatically fixes it',
          'You may get validation errors or poor performance',
          'Nothing, signatures are just suggestions',
          'The signature adapts automatically',
        ],
        correctAnswer: 1,
        explanation:
          'Mismatched data can cause validation errors or poor performance since the model expects the specified format.',
      },
      {
        id: 'q19',
        question: 'When should you include examples in signatures?',
        choices: [
          'Never, they slow things down',
          'When you want to guide model behavior with concrete examples',
          'Only during debugging',
          'Only in development mode',
        ],
        correctAnswer: 1,
        explanation:
          'Examples in signatures help guide the language model toward the desired behavior patterns.',
      },
      {
        id: 'q20',
        question: 'What is the signature spectrum approach?',
        choices: [
          'A debugging technique',
          'Progressive complexity from simple inline to detailed class-based signatures',
          'A performance optimization method',
          'A way to organize code files',
        ],
        correctAnswer: 1,
        explanation:
          'The signature spectrum represents progressive complexity from simple prototypes to production-ready specifications.',
      },
      {
        id: 'q21',
        question: 'How do you validate signature assumptions during development?',
        choices: [
          'Just run the code and hope',
          'Check input/output fields and test with minimal examples',
          'Only test in production',
          'Use automated testing only',
        ],
        correctAnswer: 1,
        explanation:
          'Validation involves checking field parsing and testing with simple, controlled examples.',
      },
      {
        id: 'q22',
        question: 'What role do field descriptions play in optimization?',
        choices: [
          'They are ignored during optimization',
          'They provide context that helps optimizers generate better prompts',
          'They slow down the optimization process',
          'They only matter for documentation',
        ],
        correctAnswer: 1,
        explanation:
          'Field descriptions provide crucial context that helps DSPy optimizers create more effective prompts.',
      },
      {
        id: 'q23',
        question: 'Can DSPy signatures be nested or hierarchical?',
        choices: [
          'No, they must always be flat',
          'Yes, complex signatures can have nested structures',
          'Only two levels deep',
          'Only with special decorators',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy supports complex signatures with nested structures for sophisticated input/output specifications.',
      },
      {
        id: 'q24',
        question: 'What is the evolution from prompts to signatures?',
        choices: [
          'Signatures are just fancy prompts',
          'Signatures provide structured interfaces vs unstructured text',
          'Signatures are slower than prompts',
          'There is no real difference',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures evolution represents moving from unstructured text prompts to structured, type-safe interfaces.',
      },
      {
        id: 'q25',
        question: 'How should you approach signature design for production systems?',
        choices: [
          'Start complex and simplify later',
          'Start simple, add structure progressively, then go professional with full context',
          'Always use the most complex form available',
          'Use only inline signatures for speed',
        ],
        correctAnswer: 1,
        explanation:
          'Best practice is to start simple for prototyping, add structure as needed, then create professional class-based signatures.',
      },

      // Core DSPy Modules (Questions 26-50)
      {
        id: 'q26',
        question: 'Which DSPy module is best for tasks requiring step-by-step reasoning?',
        choices: ['dspy.Predict', 'dspy.ChainOfThought', 'dspy.ReAct', 'dspy.Retrieve'],
        correctAnswer: 1,
        explanation:
          'dspy.ChainOfThought automatically adds reasoning steps to make the model show its work.',
      },
      {
        id: 'q27',
        question: 'What is dspy.Predict best used for?',
        choices: [
          'Complex multi-step reasoning',
          'Simple, direct tasks like classification and extraction',
          'Tasks requiring external tools',
          'Mathematical computations',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.Predict is the reliable workhorse for straightforward tasks that don\t need reasoning overhead.',
      },
      {
        id: 'q28',
        question: 'When should you choose ChainOfThought over Predict?',
        choices: [
          'When you need faster execution',
          'When you need transparent reasoning and explanation',
          'When you want to save on token costs',
          'When the task is very simple',
        ],
        correctAnswer: 1,
        explanation:
          'ChainOfThought is ideal when you need to show reasoning steps and build trust through transparency.',
      },
      {
        id: 'q29',
        question: 'What makes ProgramOfThought unique among DSPy modules?',
        choices: [
          'Its the fastest module',
          'It generates and executes actual Python code for precise computation',
          'It uses the least memory',
          'It works with any programming language',
        ],
        correctAnswer: 1,
        explanation:
          'ProgramOfThought is the computational mathematician that writes and executes code for exact results.',
      },
      {
        id: 'q30',
        question: 'Which module would you use for tasks that require using external tools?',
        choices: ['dspy.Predict', 'dspy.ChainOfThought', 'dspy.ReAct', 'dspy.ProgramOfThought'],
        correctAnswer: 2,
        explanation:
          'dspy.ReAct is designed for tool-using agents that can reason and act iteratively.',
      },
      {
        id: 'q31',
        question: 'What is the Think-Act loop in ReAct?',
        choices: [
          'A debugging process',
          'An iterative cycle of reasoning, acting, and observing',
          'A performance optimization technique',
          'A way to structure code',
        ],
        correctAnswer: 1,
        explanation:
          'ReAct uses Think → Act → Observe → Think cycles to solve problems dynamically.',
      },
      {
        id: 'q32',
        question: 'When does MultiChainComparison excel?',
        choices: [
          'For simple yes/no questions',
          'When you need multiple perspectives on complex decisions',
          'For basic text classification',
          'When speed is the only concern',
        ],
        correctAnswer: 1,
        explanation:
          'MultiChainComparison is like a wise council that provides multiple perspectives on complex problems.',
      },
      {
        id: 'q33',
        question: 'What does the M parameter control in MultiChainComparison?',
        choices: [
          'Memory usage',
          'The number of reasoning chains to generate',
          'Model temperature',
          'Maximum iterations',
        ],
        correctAnswer: 1,
        explanation:
          'The M parameter determines how many independent reasoning chains are generated for comparison.',
      },
      {
        id: 'q34',
        question: 'What is the max_iters parameter in ReAct used for?',
        choices: [
          'Setting memory limits',
          'Controlling the maximum number of think-act cycles',
          'Defining output length',
          'Setting processing timeout',
        ],
        correctAnswer: 1,
        explanation:
          'max_iters limits how many reasoning and action cycles the ReAct agent can perform.',
      },
      {
        id: 'q35',
        question: 'What are the three components typically output by ProgramOfThought?',
        choices: [
          'Input, process, output',
          'Code, result, and explanation',
          'Question, answer, confidence',
          'Data, analysis, conclusion',
        ],
        correctAnswer: 1,
        explanation:
          'ProgramOfThought outputs the generated code, computed result, and human-readable explanation.',
      },
      {
        id: 'q36',
        question: 'How does ChainOfThought improve trust in AI systems?',
        choices: [
          'By running faster',
          'By showing step-by-step reasoning that humans can follow and verify',
          'By using less computational resources',
          'By avoiding errors completely',
        ],
        correctAnswer: 1,
        explanation:
          'ChainOfThought builds trust by making AI reasoning transparent and auditable.',
      },
      {
        id: 'q37',
        question: 'What type of problems is ProgramOfThought ideal for?',
        choices: [
          'Simple text classification',
          'Mathematical problems and computational tasks requiring precision',
          'Basic question answering',
          'Image recognition',
        ],
        correctAnswer: 1,
        explanation:
          'ProgramOfThought excels at mathematical, scientific, and computational problems where precision matters.',
      },
      {
        id: 'q38',
        question: 'How does ReAct handle dynamic information gathering?',
        choices: [
          'It uses static knowledge only',
          'It can adapt its approach based on intermediate observations and tool results',
          'It follows a predetermined script',
          'It avoids external information',
        ],
        correctAnswer: 1,
        explanation:
          'ReAct can course-correct and refine its strategy based on what it learns from tool interactions.',
      },
      {
        id: 'q39',
        question: 'What is cognitive diversity in MultiChainComparison?',
        choices: [
          'Using different programming languages',
          'Generating multiple independent reasoning approaches to reduce bias',
          'Working with multiple datasets',
          'Using various AI models',
        ],
        correctAnswer: 1,
        explanation:
          'Cognitive diversity means generating different reasoning approaches to capture various perspectives on the same problem.',
      },
      {
        id: 'q40',
        question: 'Why is ProgramOfThought particularly trustworthy?',
        choices: [
          'Its always correct',
          'You can verify and understand exactly how results were calculated',
          'It uses the most advanced models',
          'Its the fastest option',
        ],
        correctAnswer: 1,
        explanation:
          'ProgramOfThought provides verifiable results because you can examine and validate the generated code.',
      },
      {
        id: 'q41',
        question: 'What makes ReAct "agentic"?',
        choices: [
          'It uses advanced algorithms',
          'It can autonomously take actions and adapt its problem-solving approach',
          'It processes multiple inputs',
          'It generates creative outputs',
        ],
        correctAnswer: 1,
        explanation:
          'ReAct is agentic because it can autonomously reason, act, and adapt in dynamic environments.',
      },
      {
        id: 'q42',
        question: 'How does temperature affect MultiChainComparison?',
        choices: [
          'Higher temperature increases diversity between reasoning chains',
          'Temperature doesn\t affect MultiChainComparison',
          'Lower temperature always produces better results',
          'Temperature only affects speed',
        ],
        correctAnswer: 0,
        explanation:
          'Higher temperature settings increase diversity between chains, while lower settings produce more consistent reasoning.',
      },
      {
        id: 'q43',
        question: 'What is algorithmic debate in MultiChainComparison?',
        choices: [
          'A debugging technique',
          'Independent reasoning chains that contribute different perspectives to synthesis',
          'A performance optimization method',
          'A way to handle errors',
        ],
        correctAnswer: 1,
        explanation:
          'Algorithmic debate involves multiple independent reasoning chains contributing perspectives for intelligent synthesis.',
      },
      {
        id: 'q44',
        question: 'When should you use ReAct sparingly?',
        choices: [
          'Never, its always the best choice',
          'When you need simple, cost-effective solutions',
          'Only in development environments',
          'When working with small datasets',
        ],
        correctAnswer: 1,
        explanation:
          'ReAct is powerful but expensive, so use simpler modules when you don\t need its full capabilities.',
      },
      {
        id: 'q45',
        question: 'What is iterative refinement in ReAct?',
        choices: [
          'Code optimization',
          'The ability to learn and adapt within a single interaction based on observations',
          'Model fine-tuning',
          'Performance improvement',
        ],
        correctAnswer: 1,
        explanation:
          'Iterative refinement allows ReAct to course-correct and improve its approach based on intermediate results.',
      },
      {
        id: 'q46',
        question: 'How should you choose between Predict and ChainOfThought?',
        choices: [
          'Always use ChainOfThought',
          'Use Predict for speed and efficiency, ChainOfThought when reasoning transparency is needed',
          'Always use Predict',
          'Choose randomly',
        ],
        correctAnswer: 1,
        explanation:
          'Choose based on whether you need the reasoning transparency that ChainOfThought provides.',
      },
      {
        id: 'q47',
        question: 'What is the synthesis process in MultiChainComparison?',
        choices: [
          'Simple averaging of results',
          'Intelligent integration that identifies themes, weighs perspectives, and handles disagreements',
          'Picking the first result',
          'Random selection from chains',
        ],
        correctAnswer: 1,
        explanation:
          'Synthesis intelligently combines multiple perspectives, not just simple averaging.',
      },
      {
        id: 'q48',
        question: 'Why is code safety important with ProgramOfThought?',
        choices: [
          'To improve performance',
          'Because it executes generated code that could potentially be unsafe',
          'To reduce memory usage',
          'To improve accuracy',
        ],
        correctAnswer: 1,
        explanation:
          'Since ProgramOfThought executes generated code, proper sandboxing and validation are crucial for security.',
      },
      {
        id: 'q49',
        question: 'What is tool orchestration in ReAct?',
        choices: [
          'Playing musical instruments',
          'Intelligently selecting, sequencing, and coordinating multiple external tools',
          'Organizing code files',
          'Managing memory usage',
        ],
        correctAnswer: 1,
        explanation:
          'Tool orchestration involves intelligently coordinating multiple tools to solve complex problems.',
      },
      {
        id: 'q50',
        question: 'How do you optimize the M parameter in MultiChainComparison?',
        choices: [
          'Always use the maximum value',
          'Balance diversity needs with computational budget (typically M=4-6 for most cases)',
          'Always use M=2',
          'Set M randomly',
        ],
        correctAnswer: 1,
        explanation:
          'M=4-6 is typically optimal for most complex decision-making scenarios, balancing diversity with efficiency.',
      },

      // Module Composition & Architecture (Questions 51-75)
      {
        id: 'q51',
        question: 'In a custom DSPy module, what method must you implement?',
        choices: ['run()', 'execute()', 'forward()', 'process()'],
        correctAnswer: 2,
        explanation:
          'Custom DSPy modules must implement the forward() method to define their main logic.',
      },
      {
        id: 'q52',
        question: 'What are the four pillars of effective module composition?',
        choices: [
          'Speed, accuracy, cost, and scalability',
          'Cohesion, coupling, flow, and resilience',
          'Input, processing, output, and feedback',
          'Planning, execution, validation, and optimization',
        ],
        correctAnswer: 1,
        explanation:
          'Effective composition requires cohesion (focused responsibility), coupling (coordination), flow (logical data movement), and resilience (error handling).',
      },
      {
        id: 'q53',
        question: 'When should you use a sequential pipeline pattern?',
        choices: [
          'Never, its too simple',
          'When your problem naturally breaks into distinct, ordered stages',
          'Only for debugging',
          'When you need maximum speed',
        ],
        correctAnswer: 1,
        explanation:
          'Sequential pipelines work well when problems have natural stage-based progression, like document processing workflows.',
      },
      {
        id: 'q54',
        question: 'What is branching logic best used for?',
        choices: [
          'Performance optimization',
          'When different types of inputs require fundamentally different processing approaches',
          'Code organization only',
          'Error handling',
        ],
        correctAnswer: 1,
        explanation:
          'Branching logic is ideal when different input types need specialized processing while maintaining a unified interface.',
      },
      {
        id: 'q55',
        question: 'How many branches is typically optimal in branching logic?',
        choices: [
          'Always 2',
          '3-7 branches for most scenarios',
          'As many as possible',
          'Exactly 10',
        ],
        correctAnswer: 1,
        explanation:
          'Usually 3-7 branches provide good specialization without excessive complexity.',
      },
      {
        id: 'q56',
        question: 'What is the ensemble methods pattern?',
        choices: [
          'Using multiple models',
          'Running multiple approaches and combining results for robustness',
          'Sequential processing',
          'Error correction',
        ],
        correctAnswer: 1,
        explanation:
          'Ensemble methods use multiple approaches simultaneously and combine their results for more robust outcomes.',
      },
      {
        id: 'q57',
        question: 'How do DSPy modules handle state?',
        choices: [
          'Through global variables only',
          'Via instance variables and learnable parameters',
          'State is not supported',
          'Through external databases only',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules manage state through instance variables and learnable parameters that can be optimized.',
      },
      {
        id: 'q58',
        question: 'What makes DSPy modules different from regular Python functions?',
        choices: [
          'They use different syntax',
          'They can be optimized and contain learnable parameters',
          'They run in different environments',
          'They require special hardware',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules can be optimized and contain learnable parameters, unlike regular Python functions.',
      },
      {
        id: 'q59',
        question: 'How do modules communicate in a DSPy pipeline?',
        choices: [
          'Through global variables',
          'Via structured input/output data flow',
          'Using message passing protocols',
          'Through shared memory only',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules communicate through well-defined input/output data flow between components.',
      },
      {
        id: 'q60',
        question: 'What is the advantage of DSPys modular approach?',
        choices: [
          'Smaller file sizes',
          'Reusability, testability, and independent optimization',
          'Faster compilation',
          'Lower memory usage',
        ],
        correctAnswer: 1,
        explanation:
          'Modularity enables reusing components, testing them independently, and optimizing each component separately.',
      },
      {
        id: 'q61',
        question: 'Can DSPy modules be nested inside other modules?',
        choices: [
          'No, they must be independent',
          'Yes, modules can contain other modules as sub-components',
          'Only with special permissions',
          'Only in development mode',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules can be composed hierarchically, with modules containing other modules as sub-components.',
      },
      {
        id: 'q62',
        question: 'What happens to DSPy modules during optimization?',
        choices: [
          'They are recompiled',
          'Their parameters and prompts are automatically improved',
          'They are replaced entirely',
          'Nothing changes',
        ],
        correctAnswer: 1,
        explanation:
          'During optimization, DSPy modules have their internal parameters and prompts improved based on training data.',
      },
      {
        id: 'q63',
        question: 'How should you approach error handling in composed modules?',
        choices: [
          'Ignore errors and hope for the best',
          'Implement proper validation and fallback strategies at each stage',
          'Only handle errors at the final output',
          'Let DSPy handle all errors automatically',
        ],
        correctAnswer: 1,
        explanation:
          'Robust systems need validation between stages and fallback strategies when components fail.',
      },
      {
        id: 'q64',
        question: 'What is recursive processing in DSPy?',
        choices: [
          'A type of error',
          'Modules that can call themselves with modified inputs for complex reasoning',
          'A performance optimization',
          'A debugging technique',
        ],
        correctAnswer: 1,
        explanation:
          'Recursive processing allows modules to call themselves iteratively to handle complex, multi-layered problems.',
      },
      {
        id: 'q65',
        question: 'How do you manage state in conversational DSPy systems?',
        choices: [
          'State is not possible',
          'Through instance variables that persist across interactions',
          'Only through external databases',
          'State resets after each interaction',
        ],
        correctAnswer: 1,
        explanation:
          'Conversational systems maintain state through instance variables that persist across multiple interactions.',
      },
      {
        id: 'q66',
        question: 'What is the philosophy of modular intelligence?',
        choices: [
          'Using only one type of thinking',
          'Creating AI systems that can think in multiple ways, like humans do',
          'Avoiding complex problems',
          'Using the largest possible models',
        ],
        correctAnswer: 1,
        explanation:
          'Modular intelligence mirrors human cognitive diversity by combining different types of thinking in one system.',
      },
      {
        id: 'q67',
        question: 'How should you test composed DSPy modules?',
        choices: [
          'Only test the final output',
          'Test individual modules and the complete pipeline separately',
          'Testing is not necessary',
          'Only test in production',
        ],
        correctAnswer: 1,
        explanation:
          'Best practice is testing both individual components and the integrated system for reliability.',
      },
      {
        id: 'q68',
        question: 'What is the "Art of Module Composition"?',
        choices: [
          'A debugging technique',
          'Orchestrating different modules like instruments in a symphony',
          'A performance optimization method',
          'A code organization system',
        ],
        correctAnswer: 1,
        explanation:
          'Module composition is like conducting a symphony - each module has expertise, but magic happens when they work together harmoniously.',
      },
      {
        id: 'q69',
        question: 'How do you maintain loose coupling in DSPy compositions?',
        choices: [
          'Use global variables everywhere',
          'Design clear interfaces and avoid internal implementation dependencies',
          'Avoid using multiple modules',
          'Hard-code all connections',
        ],
        correctAnswer: 1,
        explanation:
          'Loose coupling requires clear interfaces where modules dont depend on each others internal implementation details.',
      },
      {
        id: 'q70',
        question: 'What should each stage in a sequential pipeline do?',
        choices: [
          'Process all possible operations',
          'Transform data in one meaningful way with a focused responsibility',
          'Handle error correction only',
          'Store data permanently',
        ],
        correctAnswer: 1,
        explanation:
          'Each pipeline stage should have a focused responsibility and meaningfully transform the data in one specific way.',
      },
      {
        id: 'q71',
        question: 'How do you handle validation between pipeline stages?',
        choices: [
          'Validation is not necessary',
          'Include validation logic between stages to catch errors early',
          'Only validate at the end',
          'Let each stage validate itself only',
        ],
        correctAnswer: 1,
        explanation:
          'Validation between stages helps catch errors early and ensures data quality throughout the pipeline.',
      },
      {
        id: 'q72',
        question: 'What is the key to successful branching logic design?',
        choices: [
          'Having as many branches as possible',
          'Making routing decisions based on clear, testable criteria',
          'Using random routing',
          'Avoiding any branching',
        ],
        correctAnswer: 1,
        explanation:
          'Successful branching requires clear, testable criteria for routing decisions and consistent output formats.',
      },
      {
        id: 'q73',
        question: 'How should you approach building complex DSPy systems?',
        choices: [
          'Build everything at once',
          'Start simple and add complexity incrementally',
          'Always start with the most complex version',
          'Copy existing systems exactly',
        ],
        correctAnswer: 1,
        explanation:
          'Best practice is starting with simple versions that work, then gradually adding complexity as needed.',
      },
      {
        id: 'q74',
        question: 'What is the role of fallback strategies in module composition?',
        choices: [
          'They are not needed',
          'They provide alternative approaches when primary modules fail',
          'They are only for debugging',
          'They slow down the system',
        ],
        correctAnswer: 1,
        explanation:
          'Fallback strategies ensure system resilience by providing alternative approaches when primary methods fail.',
      },
      {
        id: 'q75',
        question: 'How do you ensure consistency across branches in branching logic?',
        choices: [
          'Use different output formats for each branch',
          'Ensure all branches produce consistent output formats',
          'Don\t worry about consistency',
          'Use only one branch',
        ],
        correctAnswer: 1,
        explanation:
          'All branches should produce consistent output formats so the system provides a unified interface regardless of routing.',
      },

      // Tools, RAG, and Production Practices (Questions 76-100)
      {
        id: 'q76',
        question: 'What does dspy.Retrieve do?',
        choices: [
          'Retrieves data from databases',
          'Finds relevant passages for RAG systems',
          'Downloads files from the internet',
          'Accesses local file systems',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.Retrieve is used in RAG (Retrieval-Augmented Generation) systems to find relevant passages.',
      },
      {
        id: 'q77',
        question: 'What is the RAG revolution?',
        choices: [
          'A new programming language',
          'Giving AI systems access to current information through retrieval',
          'A performance optimization technique',
          'A debugging method',
        ],
        correctAnswer: 1,
        explanation:
          'RAG transforms AI from closed-book to open-book systems that can access current, relevant information.',
      },
      {
        id: 'q78',
        question: 'What is multi-hop retrieval?',
        choices: [
          'Retrieving multiple files at once',
          'Iteratively retrieving information where each step informs the next query',
          'A caching mechanism',
          'A way to organize data',
        ],
        correctAnswer: 1,
        explanation:
          'Multi-hop retrieval performs iterative information gathering where each retrieval step informs subsequent queries.',
      },
      {
        id: 'q79',
        question: 'Why is reranking important in advanced RAG systems?',
        choices: [
          'It makes retrieval faster',
          'It improves the relevance of retrieved passages by scoring and filtering them',
          'It reduces memory usage',
          'It simplifies the code',
        ],
        correctAnswer: 1,
        explanation:
          'Reranking scores retrieved passages for relevance and selects the most useful ones for better generation quality.',
      },
      {
        id: 'q80',
        question: 'What should you consider when choosing tools for ReAct systems?',
        choices: [
          'Only the fastest tools',
          'Tools with clear outputs, good error handling, and composability',
          'The most complex tools available',
          'Tools that require no configuration',
        ],
        correctAnswer: 1,
        explanation:
          'Effective ReAct tools should provide clear, structured outputs and work well together.',
      },
      {
        id: 'q81',
        question: 'What is a key performance optimization tip for DSPy systems?',
        choices: [
          'Always use the most complex modules',
          'Choose the right module for each job - Predict for simple tasks, ChainOfThought when reasoning is needed',
          'Use ReAct for everything',
          'Avoid all optimizations',
        ],
        correctAnswer: 1,
        explanation:
          'Performance optimization means using simpler modules when possible and complex ones only when their capabilities are needed.',
      },
      {
        id: 'q82',
        question: 'How should you approach token usage optimization?',
        choices: [
          'Use as many tokens as possible',
          'Keep contexts concise, use max_len parameters, and summarize long inputs',
          'Ignore token usage entirely',
          'Only use single-word inputs',
        ],
        correctAnswer: 1,
        explanation:
          'Token optimization involves managing input length, output constraints, and preprocessing to reduce unnecessary token usage.',
      },
      {
        id: 'q83',
        question: 'What is the first step in debugging DSPy programs?',
        choices: [
          'Rewrite everything',
          'Isolate the problem by testing individual components',
          'Increase the model size',
          'Add more data',
        ],
        correctAnswer: 1,
        explanation:
          'Debugging starts with isolating problems by testing individual components with controlled inputs.',
      },
      {
        id: 'q84',
        question: 'What should you track when monitoring production DSPy systems?',
        choices: [
          'Only error rates',
          'Response time, token usage, error rates, and output quality scores',
          'Only system resource usage',
          'Nothing, monitoring is not necessary',
        ],
        correctAnswer: 1,
        explanation:
          'Comprehensive monitoring includes performance metrics, cost metrics, and quality metrics.',
      },
      {
        id: 'q85',
        question: 'When should you NOT use DSPy?',
        choices: [
          'Never, its always the best choice',
          'When simple deterministic rules work reliably, or for real-time low-latency requirements',
          'Only in development environments',
          'When you have unlimited resources',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy isn\t always optimal - sometimes simpler rule-based systems or traditional ML models are more appropriate.',
      },
      {
        id: 'q86',
        question: 'What is the DSPy decision tree approach?',
        choices: [
          'A random selection method',
          'A systematic framework for choosing the right DSPy components based on problem analysis',
          'A debugging technique',
          'A performance optimization method',
        ],
        correctAnswer: 1,
        explanation:
          'The decision tree provides systematic guidance for analyzing problems and selecting appropriate DSPy components.',
      },
      {
        id: 'q87',
        question: 'How should you handle caching in DSPy systems?',
        choices: [
          'Never use caching',
          'Cache repeated queries and use deterministic signatures for better hit rates',
          'Cache everything always',
          'Only cache final outputs',
        ],
        correctAnswer: 1,
        explanation:
          'Strategic caching of repeated queries and using deterministic signatures improves performance and reduces costs.',
      },
      {
        id: 'q88',
        question: 'What is important when designing custom tools for DSPy?',
        choices: [
          'Making them as complex as possible',
          'Ensuring they provide clear, structured outputs and good error handling',
          'Making them work only with specific models',
          'Avoiding any documentation',
        ],
        correctAnswer: 1,
        explanation:
          'Custom tools should be reliable, provide clear feedback, and integrate well with DSPys structured approach.',
      },
      {
        id: 'q89',
        question: 'How do you validate DSPy system assumptions during development?',
        choices: [
          'Just run it in production',
          'Test with edge cases, verify error handling, and validate output quality',
          'Only test happy path scenarios',
          'Validation is not necessary',
        ],
        correctAnswer: 1,
        explanation:
          'Thorough validation includes edge case testing, error handling verification, and output quality checks.',
      },
      {
        id: 'q90',
        question: 'What characterizes effective RAG system design?',
        choices: [
          'Using as many documents as possible',
          'Balancing retrieval quality, reranking, and generation for the specific use case',
          'Always retrieving the maximum number of passages',
          'Avoiding any filtering of results',
        ],
        correctAnswer: 1,
        explanation:
          'Effective RAG systems balance retrieval breadth, relevance filtering, and generation quality for optimal results.',
      },
      {
        id: 'q91',
        question: 'How should you approach error handling in production DSPy systems?',
        choices: [
          'Let errors crash the system',
          'Implement graceful degradation and fallback strategies',
          'Only log errors',
          'Ignore all errors',
        ],
        correctAnswer: 1,
        explanation:
          'Production systems need graceful error handling with fallbacks to maintain service availability.',
      },
      {
        id: 'q92',
        question: 'What is the benefit of logging in DSPy systems?',
        choices: [
          'It slows down the system',
          'It provides visibility into system behavior for debugging and optimization',
          'Its only useful for developers',
          'It has no real benefit',
        ],
        correctAnswer: 1,
        explanation:
          'Logging provides crucial visibility into how DSPy systems behave, enabling debugging and performance optimization.',
      },
      {
        id: 'q93',
        question: 'How do you build incrementally with DSPy?',
        choices: [
          'Build everything at once',
          'Start with simple versions, add complexity gradually, test each component',
          'Always start with the most complex version',
          'Copy existing solutions exactly',
        ],
        correctAnswer: 1,
        explanation:
          'Incremental building starts simple, adds complexity as needed, and tests each component independently.',
      },
      {
        id: 'q94',
        question: 'What makes DSPy systems extensible?',
        choices: [
          'Using only built-in modules',
          'Modular design that allows adding new components without changing existing ones',
          'Hard-coding all functionality',
          'Avoiding any customization',
        ],
        correctAnswer: 1,
        explanation:
          'DSPys modular architecture allows extending systems by adding new components without disrupting existing functionality.',
      },
      {
        id: 'q95',
        question: 'How do you ensure data quality in RAG systems?',
        choices: [
          'Use all available data without filtering',
          'Implement relevance scoring, content validation, and source quality assessment',
          'Only use the newest data',
          'Avoid any quality checks',
        ],
        correctAnswer: 1,
        explanation:
          'RAG data quality requires relevance scoring, content validation, and assessing source reliability.',
      },
      {
        id: 'q96',
        question: 'What is the role of citations in advanced RAG systems?',
        choices: [
          'Citations are not important',
          'They provide transparency and allow users to verify information sources',
          'They slow down the system',
          'They are only for academic use',
        ],
        correctAnswer: 1,
        explanation:
          'Citations enable transparency and verification, building trust in RAG system outputs.',
      },
      {
        id: 'q97',
        question: 'How should you handle model updates in production DSPy systems?',
        choices: [
          'Updates are automatic',
          'Test thoroughly and potentially reoptimize since different models may behave differently',
          'Never update models',
          'Update without testing',
        ],
        correctAnswer: 1,
        explanation:
          'Model updates require careful testing and potential reoptimization since different models can behave very differently.',
      },
      {
        id: 'q98',
        question: 'What is important for long-term success with DSPy?',
        choices: [
          'Using only the newest models',
          'Building systematic processes for development, optimization, and maintenance',
          'Avoiding any planning',
          'Working in isolation',
        ],
        correctAnswer: 1,
        explanation:
          'Long-term success requires systematic processes for building, optimizing, and maintaining DSPy systems.',
      },
      {
        id: 'q99',
        question: 'How do you maintain DSPy systems in production?',
        choices: [
          'They maintain themselves automatically',
          'Regular monitoring, evaluation, and reoptimization as conditions change',
          'Only update when systems break',
          'Maintenance is not required',
        ],
        correctAnswer: 1,
        explanation:
          'Production DSPy systems need ongoing monitoring, evaluation, and reoptimization as requirements and conditions evolve.',
      },
      {
        id: 'q100',
        question: 'What is the key principle for architecting DSPy systems?',
        choices: [
          'Always use the most complex approach',
          'Balance simplicity and sophistication - use the right tool for each job',
          'Avoid any architectural planning',
          'Copy existing architectures exactly',
        ],
        correctAnswer: 1,
        explanation:
          'Good DSPy architecture balances simplicity and sophistication, using appropriate complexity for each component based on its requirements.',
      },
    ],
  },
};
